import React, { useState, useEffect, useCallback, useMemo } from "react";
import * as XLSX from "xlsx";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getAlunni, getAlunno, getAnniDisponibili, getFiltriClassi, getAlunniCountsBySettore, getAlunniCountsByCiclo, getAlunniDashboardAddebiti, getAlunniDashboardAddebitiSerie } from "@/lib/api";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/utils";
import { useAuth } from "@/lib/AuthContext";
import AlunnoEmailsModal from "@/components/AlunnoEmailsModal";
import AgevolazioneModal from "@/components/AgevolazioneModal";
import AgevMailModal from "@/components/AgevMailModal";
import AnnoSelector from "@/components/AnnoSelector";
import DocumentiAlunnoModal from "@/components/DocumentiAlunnoModal";
import ConfiguratoreAgevStandard from "@/components/ConfiguratoreAgevStandard";
import AlunniSendBar from "@/components/AlunniSendBar";
import IscrizioneBadge from "@/components/IscrizioneBadge";
import MovimentiCollegatiModal from "@/components/MovimentiCollegatiModal";
import SchedaRiassuntiva from "@/components/SchedaRiassuntiva";
import useAlunniSelection from "@/lib/useAlunniSelection";
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

// Formatter helpers per il mini-grafico dashboard addebiti
const _fmtEur = (n) => Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const _fmtEurK = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(0)}k`;
  return `${v.toFixed(0)}`;
};

// Configurazione stati scheda agevolazione (label UI + colori).
// APPROVATA è mappato a "CONFERMATA" lato UI (richiesta utente feb-2026).
const AGEV_STATI_CFG = {
  APPROVATA:    { ui: "CONFERMATA",   bg: "#e8f5e9", color: "#1b5e20" },
  BOZZA:        { ui: "BOZZA",        bg: "#fff3e0", color: "#ef6c00" },
  NO:           { ui: "NO",           bg: "#ffebee", color: "#c62828" },
  USCITO:       { ui: "USCITO",       bg: "#eceff1", color: "#455a64" },
  APPUNTAMENTO: { ui: "APPUNTAMENTO", bg: "#ede7f6", color: "#4527a0" },
  RATE:         { ui: "RATE",         bg: "#e3f2fd", color: "#0d47a1" },
  SC_FRATELLI:  { ui: "SC. FRATELLI", bg: "#fff8e1", color: "#bf360c" },
  FIRMATA:      { ui: "FIRMATA",      bg: "#e0f7fa", color: "#006064" },
};
const AGEV_STATI_OPTIONS = [
  { value: "BOZZA",        label: "BOZZA" },
  { value: "CONFERMATA",   label: "CONFERMATA" },
  { value: "NO",           label: "NO" },
  { value: "USCITO",       label: "USCITO" },
  { value: "APPUNTAMENTO", label: "APPUNT." },
  { value: "RATE",         label: "RATE" },
  { value: "SC_FRATELLI",  label: "SC. FRATELLI" },
  { value: "FIRMATA",      label: "FIRMATA" },
];
function agevStatoCfg(st) {
  return AGEV_STATI_CFG[st] || AGEV_STATI_CFG.BOZZA;
}
// Restituisce il valore da usare come `<select value>` per uno stato backend.
// Unica trasformazione: APPROVATA è esposto come "CONFERMATA" lato UI (alias storico).
function agevSelectValue(st) {
  if (!st) return "BOZZA";
  return st === "APPROVATA" ? "CONFERMATA" : st;
}


function GenitoriAlunnoModal({ alunno, onClose }) {
  const [genitori, setGenitori] = useState([]);
  const [dettaglio, setDettaglio] = useState(null); // dati anagrafici completi alunno
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const cod = alunno?.Cod;
  const anno = alunno?.anno;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr("");
      try {
        const r = await getAlunno(cod, anno);
        if (!cancelled) {
          setGenitori(r.data?.genitori || []);
          // L'endpoint ritorna {alunno, genitori, classi, movimenti, ...}:
          // i campi anagrafici sono dentro `alunno`
          setDettaglio(r.data?.alunno || null);
        }
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || "Errore caricamento");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cod, anno]);

  // Merge dati: prop alunno (lista) + dettaglio API
  const A = { ...(alunno || {}), ...(dettaglio || {}) };
  const nato_il = (A.NasDat || "").trim();
  const indirizzo = (A.ResVia || "").trim();
  const cap_cit_prov = [A.ResCap, A.ResLuo || A.ResLoc, A.ResPro ? `(${A.ResPro})` : ""]
    .filter(Boolean).join(" ").trim();
  const g1 = genitori[0];
  const g2 = genitori[1];

  return (
    <div data-testid="genitori-alunno-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={onClose}>
      <div style={{ background: "white", width: "min(900px, 95vw)", maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column", borderRadius: 4 }}
        onClick={e => e.stopPropagation()}>
        <div style={{ background: "#00838f", color: "white", padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <strong>👤 {alunno?.Cog} {alunno?.Nom} <span style={{ opacity: 0.85, fontWeight: 400 }}>· Cod. {cod}</span></strong>
          <button onClick={onClose} data-testid="genitori-alunno-close"
            style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 18 }}>✕</button>
        </div>
        {/* TESTATA ANAGRAFICA — feb 2026 */}
        <div data-testid="alunno-anagrafica-header"
          style={{ background: "#e0f7fa", borderBottom: "1px solid #b2ebf2", padding: "10px 16px", fontSize: 11.5, lineHeight: 1.55, color: "#004d40" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: "2px 24px" }}>
            <div><b>Nato a:</b> {A.NasLuo || "—"}{A.NasPro ? ` (${A.NasPro})` : ""}</div>
            <div><b>Nato il:</b> {nato_il || "—"}</div>
            <div style={{ gridColumn: "1 / span 2" }}><b>Indirizzo:</b> {indirizzo || "—"}</div>
            <div style={{ gridColumn: "1 / span 2" }}><b>CAP Città Prov.:</b> {cap_cit_prov || "—"}</div>
            <div style={{ gridColumn: "1 / span 2", fontFamily: "Consolas, monospace" }}><b>Codice Fiscale:</b> {A.CodFis || "—"}</div>
            <div data-testid="alunno-genitore1" style={{ gridColumn: "1 / span 2", borderTop: "1px solid #b2ebf2", marginTop: 4, paddingTop: 4 }}>
              <b>Genitore 1:</b> {g1 ? `${g1.Cog || ""} ${g1.Nom || ""}`.trim() : "—"}
              {g1?.CodFis && <span style={{ marginLeft: 6, fontFamily: "Consolas, monospace", color: "#00695c" }}>· CF {g1.CodFis}</span>}
            </div>
            <div data-testid="alunno-genitore2" style={{ gridColumn: "1 / span 2" }}>
              <b>Genitore 2:</b> {g2 ? `${g2.Cog || ""} ${g2.Nom || ""}`.trim() : "—"}
              {g2?.CodFis && <span style={{ marginLeft: 6, fontFamily: "Consolas, monospace", color: "#00695c" }}>· CF {g2.CodFis}</span>}
            </div>
          </div>
        </div>
        <div style={{ padding: 12, overflow: "auto" }}>
          {loading && <div style={{ padding: 20, textAlign: "center", color: "#666" }}>Caricamento…</div>}
          {err && <div style={{ padding: 10, background: "#ffebee", border: "1px solid #ef9a9a", color: "#c62828", fontSize: 12 }}>{err}</div>}
          {!loading && !err && genitori.length === 0 && (
            <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Nessun genitore registrato per questo alunno.</div>
          )}
          {!loading && !err && genitori.length > 0 && (
            <table className="sw-table">
              <thead>
                <tr><th>Cod</th><th>Cognome</th><th>Nome</th><th>CF</th><th>Cellulare</th><th>Email</th><th>P.IVA</th></tr>
              </thead>
              <tbody>
                {genitori.map((g, i) => (
                  <tr key={`${g.Cod}-${i}`} data-testid={`alu-genitore-row-${g.Cod}`}>
                    <td>{g.Cod}</td>
                    <td style={{ fontWeight: 600 }}>{g.Cog}</td>
                    <td>{g.Nom}</td>
                    <td style={{ fontSize: 11, fontFamily: "Consolas" }}>{g.CodFis || "-"}</td>
                    <td>{g.Cel || g.Tel1 || "-"}</td>
                    <td>{g.Mai || "-"}</td>
                    <td>{g.ParIva || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function AlunnoDetail({ cod, anno, onClose, gestione }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // 🆕 Feb 2026: modifica inline settore/classe (invece che deep-link a Curriculum)
  const [editSet, setEditSet] = useState("");
  const [editCla, setEditCla] = useState("");
  const [savingSC, setSavingSC] = useState(false);
  const [scMsg, setScMsg] = useState(null);
  // 🆕 Lista settori/classi disponibili per dropdown
  const [settoriList, setSettoriList] = useState([]);
  const [classiPerSettore, setClassiPerSettore] = useState({}); // { "A": ["1COCC", "1RICC", ...] }
  // 🆕 Feb 2026: gestione INLINE email genitori (invece che modal separato)
  const [emails, setEmails] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [newNote, setNewNote] = useState("");
  const [addingEmail, setAddingEmail] = useState(false);
  const [emailErr, setEmailErr] = useState(null);
  // 🆕 Rettifica nome/cognome (correzione refusi anagrafici, propaga a tutti gli anni)
  const [editNome, setEditNome] = useState(false);
  const [nomeVal, setNomeVal] = useState("");
  const [cogVal, setCogVal] = useState("");
  const [savingNome, setSavingNome] = useState(false);
  const [nomeMsg, setNomeMsg] = useState(null);

  const caricaEmails = async () => {
    setEmailsLoading(true);
    try {
      const r = await api.get(`/alunni-emails/${cod}`);
      setEmails(r.data?.items || []);
    } catch (e) {
      setEmailErr(e.response?.data?.detail || "Errore caricamento email");
    } finally {
      setEmailsLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await getAlunno(cod, anno);
        setData(res.data);
        const c0 = (res.data?.classi || [])[0];
        setEditSet(c0?.SetNom || "");
        setEditCla(c0?.ClaNom || "");
      }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
    caricaEmails();
    // 🆕 Carica lista settori/classi per popolare i dropdown
    (async () => {
      try {
        const r = await getFiltriClassi(anno);
        setSettoriList(r.data?.settori || []);
        // r.data.classi è tipo ["1COCC (A)", "1A (E)", ...] → raggruppa per settore
        const grouped = {};
        (r.data?.classi || []).forEach((s) => {
          const m = /^(.+?)\s*\(([^)]+)\)\s*$/.exec(s);
          if (m) {
            const cla = m[1].trim();
            const set = m[2].trim();
            if (!grouped[set]) grouped[set] = [];
            if (!grouped[set].includes(cla)) grouped[set].push(cla);
          }
        });
        setClassiPerSettore(grouped);
      } catch (e) { console.warn("getFiltriClassi", e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cod, anno]);
  if (loading) return <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.3)",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{background:"white",padding:24,borderRadius:4,width:600}}>Caricamento...</div></div>;
  if (!data) return null;
  const { alunno: a, classi } = data;

  const apriEditNome = () => {
    setCogVal(a.Cog || "");
    setNomeVal(a.Nom || "");
    setNomeMsg(null);
    setEditNome(true);
  };
  const salvaNome = async () => {
    const cog = (cogVal || "").trim();
    const nom = (nomeVal || "").trim();
    if (!cog || !nom) { setNomeMsg({ err: "Cognome e Nome obbligatori" }); return; }
    setSavingNome(true); setNomeMsg(null);
    try {
      const r = await api.put(`/alunni/${a.Cod}/anagrafica-nome`, { cognome: cog, nome: nom });
      setNomeMsg({ ok: `Aggiornato (${r.data.record_aggiornati} anni)` });
      setEditNome(false);
      try { const res = await getAlunno(cod, anno); setData(res.data); } catch { /* ignore */ }
    } catch (e) {
      setNomeMsg({ err: e.response?.data?.detail || e.message });
    } finally {
      setSavingNome(false);
    }
  };

  const salvaSettoreClasse = async () => {
    const set = (editSet || "").trim().toUpperCase();
    const cla = (editCla || "").trim().toUpperCase();
    if (!set || !cla) { setScMsg({ err: "Settore e Classe obbligatori" }); return; }
    setSavingSC(true); setScMsg(null);
    try {
      await api.put(`/scuola-curriculum/${a.Cod}/snap`, { anno: Number(anno), settore: set, classe: cla });
      setScMsg({ ok: `Salvato: ${cla} - ${set}` });
      try { const res = await getAlunno(cod, anno); setData(res.data); } catch { /* ignore */ }
    } catch (e) {
      setScMsg({ err: e.response?.data?.detail || e.message });
    } finally {
      setSavingSC(false);
    }
  };

  const aggiungiEmail = async () => {
    if (!newEmail || !newEmail.includes("@")) { setEmailErr("Email non valida"); return; }
    setAddingEmail(true); setEmailErr(null);
    try {
      await api.post(`/alunni-emails/${cod}`, { email: newEmail, note: newNote, source: "manual" });
      setNewEmail(""); setNewNote("");
      await caricaEmails();
    } catch (e) {
      setEmailErr(e.response?.data?.detail || "Errore aggiunta");
    } finally {
      setAddingEmail(false);
    }
  };

  const eliminaEmail = async (eid) => {
    if (!window.confirm("Eliminare questa email?")) return;
    try {
      await api.delete(`/alunni-emails/${cod}/${eid}`);
      await caricaEmails();
    } catch (e) {
      setEmailErr(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  const badgeOrigine = {
    segremat:    { c: "#1565c0", bg: "#e3f2fd", l: "Segremat" },
    import:      { c: "#558b2f", bg: "#f1f8e9", l: "Importata" },
    manual:      { c: "#6a1b9a", bg: "#f3e5f5", l: "Manuale" },
    comunicator: { c: "#bf360c", bg: "#fff3e0", l: "Comunicator" },
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.3)",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={onClose}>
      <div style={{background:"white",border:"1px solid #ccc",width:760,maxHeight:"85vh",overflow:"auto"}} onClick={e=>e.stopPropagation()}>
        <div style={{background:"#0066cc",color:"white",padding:"10px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <strong data-testid={`alunno-detail-title-${a.Cod}`}>{a.Cog} {a.Nom} - Cod. {a.Cod}</strong>
          <button onClick={onClose} style={{background:"none",border:"none",color:"white",cursor:"pointer",fontSize:18}}>✕</button>
        </div>
        <div style={{padding:16}}>
          {/* 🆕 Rettifica refuso nome/cognome (propaga a tutti gli anni, corregge anche il Portale Alunni) */}
          <div style={{ background: "#fff8e1", border: "1px solid #ffcc80", borderRadius: 3, padding: 10, marginBottom: 10 }}>
            {!editNome ? (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <div style={{ fontSize: 12, color: "#6d4c00" }}>
                  <b>Anagrafica:</b> {a.Cog} {a.Nom}
                  <span style={{ color: "#999" }}> — correggi qui eventuali refusi (aggiorna anche il Portale Alunni)</span>
                </div>
                <button onClick={apriEditNome} data-testid={`btn-edit-nome-${a.Cod}`}
                  style={{ padding: "4px 10px", background: "#f57f17", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                  ✏️ Correggi nome/cognome
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#6d4c00" }}>Cognome:</label>
                <input value={cogVal} onChange={(e) => setCogVal(e.target.value)} data-testid={`input-cognome-${a.Cod}`}
                  style={{ padding: "5px 8px", border: "1px solid #ffb74d", borderRadius: 3, fontSize: 12, textTransform: "uppercase", width: 180 }} />
                <label style={{ fontSize: 11, fontWeight: 700, color: "#6d4c00" }}>Nome:</label>
                <input value={nomeVal} onChange={(e) => setNomeVal(e.target.value)} data-testid={`input-nome-${a.Cod}`}
                  style={{ padding: "5px 8px", border: "1px solid #ffb74d", borderRadius: 3, fontSize: 12, textTransform: "uppercase", width: 180 }} />
                <button onClick={salvaNome} disabled={savingNome} data-testid={`btn-salva-nome-${a.Cod}`}
                  style={{ padding: "5px 12px", background: "#2e7d32", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                  {savingNome ? "Salvataggio…" : "✓ Salva"}
                </button>
                <button onClick={() => setEditNome(false)} disabled={savingNome}
                  style={{ padding: "5px 12px", background: "white", color: "#555", border: "1px solid #ccc", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                  Annulla
                </button>
              </div>
            )}
            {nomeMsg?.err && <div data-testid={`nome-err-${a.Cod}`} style={{ marginTop: 6, color: "#b71c1c", fontSize: 11, fontWeight: 700 }}>⚠ {nomeMsg.err}</div>}
            {nomeMsg?.ok && <div data-testid={`nome-ok-${a.Cod}`} style={{ marginTop: 6, color: "#1b5e20", fontSize: 11, fontWeight: 700 }}>✓ {nomeMsg.ok}</div>}
          </div>
          <table className="sw-table" style={{marginBottom:12}}>
            <tbody>
              <tr><td style={{fontWeight:600,width:120}}>CF</td><td>{a.CodFis||"-"}</td><td style={{fontWeight:600,width:120}}>Nascita</td><td>{a.NasDat||"-"} {a.NasLuo||""}</td></tr>
              <tr><td style={{fontWeight:600}}>Residenza</td><td>{a.ResVia||"-"}, {a.ResCap||""} {a.ResLuo||""}</td><td style={{fontWeight:600}}>Telefono</td><td>{a.Tel1||a.Tel2||"-"}</td></tr>
            </tbody>
          </table>

          {/* 🆕 Sezione modifica INLINE settore/classe (feb 2026) */}
          <div style={{ background: "#f5f5f5", border: "1px solid #d0d0d0", borderRadius: 3, padding: 10, marginBottom: 10 }}>
            <div style={{ fontWeight: 700, marginBottom: 6, color: "#0d47a1" }}>
              Classe &amp; Settore — A.S. {anno}/{Number(anno) + 1}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#37474f" }}>Settore:</label>
              <select value={editSet}
                onChange={(e) => { setEditSet(e.target.value); setEditCla(""); }}
                data-testid={`edit-settore-${a.Cod}`}
                style={{ padding: "4px 8px", fontSize: 13, fontWeight: 700, border: "1px solid #90a4ae", borderRadius: 2, minWidth: 100, background: "white" }}>
                <option value="">— seleziona —</option>
                {settoriList.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#37474f", marginLeft: 8 }}>Classe:</label>
              <select value={editCla}
                onChange={(e) => setEditCla(e.target.value)}
                disabled={!editSet}
                data-testid={`edit-classe-${a.Cod}`}
                style={{ padding: "4px 8px", fontSize: 13, fontWeight: 700, border: "1px solid #90a4ae", borderRadius: 2, minWidth: 130, background: editSet ? "white" : "#eceff1" }}>
                <option value="">— seleziona —</option>
                {(classiPerSettore[editSet] || []).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <button onClick={salvaSettoreClasse} disabled={savingSC || !editSet || !editCla}
                data-testid={`btn-salva-settore-classe-${a.Cod}`}
                style={{ background: (savingSC || !editSet || !editCla) ? "#90a4ae" : "#2e7d32", color: "white", border: "none", padding: "5px 14px", fontSize: 12, fontWeight: 700, cursor: (savingSC || !editSet || !editCla) ? "not-allowed" : "pointer", borderRadius: 3 }}>
                {savingSC ? "Salvataggio…" : "💾 Salva"}
              </button>
              {classi?.length > 0 && (
                <span style={{ fontSize: 11, color: "#546e7a", marginLeft: 8 }}>
                  Attuale: <b>{classi.map(c => `${c.ClaNom} - ${c.SetNom}`).join(", ")}</b>
                </span>
              )}
            </div>
            {scMsg?.err && (
              <div data-testid={`sc-err-${a.Cod}`} style={{ marginTop: 6, color: "#b71c1c", fontSize: 11, fontWeight: 700 }}>⚠ {scMsg.err}</div>
            )}
            {scMsg?.ok && (
              <div data-testid={`sc-ok-${a.Cod}`} style={{ marginTop: 6, color: "#1b5e20", fontSize: 11, fontWeight: 700 }}>✓ {scMsg.ok}</div>
            )}
          </div>

          {/* 🆕 Gestione INLINE email genitori (feb 2026) — sostituisce AlunnoEmailsModal */}
          <div style={{ background: "#e0f2f1", border: "2px solid #00838f", borderRadius: 4, padding: 12, boxShadow: "0 2px 6px rgba(0,131,143,0.25)" }}>
            <div style={{ fontWeight: 900, marginBottom: 10, color: "white", background: "#00838f", padding: "6px 12px", borderRadius: 3, fontSize: 14, letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 18 }}>📧</span>
              <span>GESTISCI EMAIL GENITORI</span>
              <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 600, background: "rgba(255,255,255,0.25)", padding: "2px 8px", borderRadius: 10 }}>
                {emails.length} {emails.length === 1 ? "email" : "email"}
              </span>
            </div>
            {emailErr && (
              <div style={{ marginBottom: 8, padding: 6, background: "#fff0f0", color: "#c00", fontSize: 11, border: "1px solid #fcc", borderRadius: 3 }}>
                {emailErr}
                <button onClick={() => setEmailErr(null)} style={{ marginLeft: 8, border: "none", background: "none", color: "#c00", cursor: "pointer", fontWeight: 700 }}>✕</button>
              </div>
            )}
            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse", background: "white", border: "1px solid #ddd", marginBottom: 10 }}>
              <thead style={{ background: "#1f3864", color: "white" }}>
                <tr>
                  <th style={{ padding: 5, textAlign: "left" }}>EMAIL</th>
                  <th style={{ padding: 5, width: 90, textAlign: "center" }}>ORIGINE</th>
                  <th style={{ padding: 5, width: 130 }}>NOTE</th>
                  <th style={{ padding: 5, width: 45 }}></th>
                </tr>
              </thead>
              <tbody>
                {emailsLoading && <tr><td colSpan={4} style={{ padding: 10, textAlign: "center", color: "#888" }}>Caricamento…</td></tr>}
                {!emailsLoading && emails.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: 10, textAlign: "center", color: "#888" }}>Nessuna email registrata.</td></tr>
                )}
                {!emailsLoading && emails.map(it => {
                  const b = badgeOrigine[it.source] || badgeOrigine.manual;
                  return (
                    <tr key={it.id} data-testid={`aluemail-row-${it.id}`} style={{ borderTop: "1px solid #eee" }}>
                      <td style={{ padding: 5, fontFamily: "Consolas, monospace", color: "#0d47a1" }}>{it.email}</td>
                      <td style={{ padding: 5, textAlign: "center" }}>
                        <span style={{ padding: "1px 6px", background: b.bg, color: b.c, fontSize: 10, fontWeight: 700, borderRadius: 2 }}>{b.l}</span>
                      </td>
                      <td style={{ padding: 5, fontSize: 11, color: "#666" }}>{it.note || ""}</td>
                      <td style={{ padding: 5, textAlign: "center" }}>
                        <button onClick={() => eliminaEmail(it.id)} data-testid={`aluemail-del-${it.id}`}
                          title={it.source === "segremat" ? "L'email Segremat sarà ri-aggiunta al prossimo sync" : "Elimina"}
                          style={{ background: "#c62828", color: "white", border: "none", padding: "2px 6px", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>🗑</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
                placeholder="nuova@email.it" data-testid={`aluemail-new-${a.Cod}`}
                onKeyDown={e => { if (e.key === "Enter") aggiungiEmail(); }}
                style={{ flex: 1, minWidth: 220, padding: 6, border: "1px solid #ccc", fontSize: 12, fontFamily: "Consolas, monospace" }} />
              <input type="text" value={newNote} onChange={e => setNewNote(e.target.value)}
                placeholder="Nota (opzionale)"
                style={{ flex: 0.6, minWidth: 130, padding: 6, border: "1px solid #ccc", fontSize: 12 }} />
              <button onClick={aggiungiEmail} disabled={addingEmail || !newEmail}
                data-testid={`aluemail-add-${a.Cod}`}
                style={{ background: (addingEmail || !newEmail) ? "#bbb" : "#00838f", color: "white", border: "none", padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: addingEmail ? "wait" : "pointer", borderRadius: 3 }}>
                {addingEmail ? "…" : "+ Aggiungi"}
              </button>
            </div>
          </div>
          {/* 🗑 Sezioni "Genitori" e "Ultimi Movimenti" rimosse su richiesta utente feb-2026 */}
        </div>
      </div>
    </div>
  );
}

// 🆕 Ago 2026: dropdown multi-selezione con checkbox (per filtri settore/classe).
function MultiCheckDropdown({ testId, placeholder, options, selected, onToggle, onClear, minWidth = 150 }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const selCount = selected.length;
  const summary = selCount === 0
    ? placeholder
    : (selCount === 1
        ? (options.find(o => o.value === selected[0])?.label || selected[0])
        : `${selCount} selez.`);
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button type="button" data-testid={testId} onClick={() => setOpen(o => !o)}
        className="sw-filter-select"
        style={{ minWidth, textAlign: "left", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{summary}</span>
        <span style={{ fontSize: 10, opacity: 0.7 }}>▼</span>
      </button>
      {open && (
        <div data-testid={`${testId}-panel`} style={{
          position: "absolute", zIndex: 50, top: "calc(100% + 4px)", left: 0,
          background: "white", border: "1px solid #cfd8dc", borderRadius: 6,
          boxShadow: "0 6px 20px rgba(0,0,0,0.15)", padding: 6, minWidth: Math.max(minWidth, 180),
          maxHeight: 320, overflowY: "auto",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "2px 6px 6px" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#607d8b" }}>{placeholder}</span>
            {selCount > 0 && (
              <button type="button" onClick={onClear}
                style={{ fontSize: 11, color: "#c62828", background: "none", border: "none", cursor: "pointer", fontWeight: 700 }}>
                Pulisci
              </button>
            )}
          </div>
          {options.map(o => {
            const checked = selected.includes(o.value);
            return (
              <label key={o.value} data-testid={`${testId}-opt-${o.value}`}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 6px", cursor: "pointer", fontSize: 13, borderRadius: 4, background: checked ? "#e3f2fd" : "transparent" }}>
                <input type="checkbox" checked={checked} onChange={() => onToggle(o.value)} />
                <span>{o.label}</span>
              </label>
            );
          })}
          {options.length === 0 && <div style={{ padding: 8, fontSize: 12, color: "#90a4ae" }}>Nessuna opzione</div>}
        </div>
      )}
    </div>
  );
}


export default function AlunniPage({ initialAgevFilter = "", titlePrefix = "" }) {
  const { annoCorrente } = useAuth();
  const { gestione } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const showTotali = (gestione === "segremat" || gestione === "admin") && initialAgevFilter !== "agev";
  const [alunni, setAlunni] = useState([]);
  // 🆕 Movimenti Iscrizione (Cau=1) per ogni alunno visibile — batch fetch
  const [iscrCounts, setIscrCounts] = useState({});
  const [iscrModal, setIscrModal] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedCod, setSelectedCod] = useState(null);
  const [emailsCod, setEmailsCod] = useState(null);
  const [docsAlunno, setDocsAlunno] = useState(null); // alunno object o null
  // 🆕 Scheda Alunno (portale): toggle globale + modale per-alunno
  const [schedaEnabled, setSchedaEnabled] = useState(false);
  const [schedaSaving, setSchedaSaving] = useState(false);
  const [schedaAlunno, setSchedaAlunno] = useState(null); // {cod, anno, label} | null
  const [emailsLabel, setEmailsLabel] = useState("");
  const [localAnno, setLocalAnno] = useState(null);
  const [anniList, setAnniList] = useState([]);
  const sel = useAlunniSelection();
  const [settoreFilter, setSettoreFilter] = useState([]);
  const [cicloFilter, setCicloFilter] = useState("");
  const [cicloCounts, setCicloCounts] = useState({});
  const [totalAllCicli, setTotalAllCicli] = useState(0);
  const [classeFilter, setClasseFilter] = useState([]);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportSel, setExportSel] = useState(() => {
    try { const raw = localStorage.getItem("alunni_export_fields_v1"); if (raw) return JSON.parse(raw); } catch (e) { /* ignore */ }
    return null;
  });
  const [settoriList, setSettoriList] = useState([]);
  const [settoriCounts, setSettoriCounts] = useState({}); // {A: 109, E: 118, ...}
  const [totalAllSettori, setTotalAllSettori] = useState(0);
  // 🆕 Feb 2026: Dashboard totali addebiti (aggiornata per settore selezionato)
  const [dashAddeb, setDashAddeb] = useState(null);
  const [dashAddebLoading, setDashAddebLoading] = useState(false);
  // 🆕 Serie mensile per il mini-grafico storico
  const [dashSerie, setDashSerie] = useState([]);
  const [showChart, setShowChart] = useState(true);
  const [classiList, setClassiList] = useState([]);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("desc");
  // Ordinamento principale lista alunni
  // - Default standard: settore → classe → cognome → nome
  // - Default in modalità Agevolazioni: cognome → nome (richiesta utente)
  const [sortMode, setSortMode] = useState(initialAgevFilter === "agev" ? "cog" : "settore_classe");
  // Raggruppamento per settore/classe (default ON, ma OFF in Agevolazioni perché l'ordine richiesto è cognome+nome)
  const [raggruppato, setRaggruppato] = useState(initialAgevFilter !== "agev");
  const [genitoriAlunno, setGenitoriAlunno] = useState(null);
  const [showConfigAgev, setShowConfigAgev] = useState(false);
  const [agevCod, setAgevCod] = useState(null);
  const [agevMail, setAgevMail] = useState(null);
  const [agevAnno, setAgevAnno] = useState(null); // anno scolastico scelto per la scheda da aprire
  const [agevPicker, setAgevPicker] = useState(null); // { cod } -> mostra picker anno per nuova scheda
  const [agevActiveSet, setAgevActiveSet] = useState(new Set());
  const [agevStati, setAgevStati] = useState({});
  const [agevDote, setAgevDote] = useState({});
  const [agevTot, setAgevTot] = useState({});
  const [agevNetta, setAgevNetta] = useState({});
  const [agevVerificata, setAgevVerificata] = useState({}); // {cod: bool} anno corrente
  // Filtro "Anno ISEE": determina di QUALE anno mostrare la colonna ISEE e il flag MANC.
  // Default = anno corrente (anno solare). L'utente può selezionare anni passati/futuri.
  const [iseeFilterAnno, setIseeFilterAnno] = useState(() => new Date().getFullYear());
  // Mappa ISEE caricata in base a iseeFilterAnno
  const [iseeMap, setIseeMap] = useState({}); // {CodFis(upper): importo} per iseeFilterAnno
  // Mappa "ISEE Mancante" per iseeFilterAnno (cod -> bool)
  const [iseeMancanteFiltered, setIseeMancanteFiltered] = useState({});
  const [dichInviataFiltered, setDichInviataFiltered] = useState({});
  const [dichFirmataFiltered, setDichFirmataFiltered] = useState({});
  // Mappe per ANNO+1 (blocco aggiuntivo visibile solo in isAgevMode)
  const [agevActiveSetNext, setAgevActiveSetNext] = useState(new Set());
  const [agevStatiNext, setAgevStatiNext] = useState({});
  const [agevDoteNext, setAgevDoteNext] = useState({});
  const [agevNettaNext, setAgevNettaNext] = useState({});
  const [agevVerificataNext, setAgevVerificataNext] = useState({});
  // 🆕 Feb 2026: Mappe per ANNO−1 (colonna OK-Prev in isAgevMode, per verificare
  // schede anno precedente accanto a corrente e successivo).
  const [agevActiveSetPrev, setAgevActiveSetPrev] = useState(new Set());
  const [agevVerificataPrev, setAgevVerificataPrev] = useState({});
  // 🆕 Feb 2026: Set globale (tutti gli anni) — usato per filtro "Tutti" in Agevolazioni
  const [agevActiveSetAny, setAgevActiveSetAny] = useState(new Set());
  const [filtroAgev, setFiltroAgev] = useState(initialAgevFilter || "");
  // 🆕 Feb 2026: filtro "Anno Agev." per restringere gli alunni mostrati a quelli con scheda
  // agevolazione per uno specifico A.S. (o "Tutti" = qualsiasi anno).
  // Valori: "" (Tutti = union tutti gli anni), "prev" (A-1), "cur" (A), "next" (A+1).
  // Default in modalità Agevolazioni: "cur" (mostra solo agevolati dell'A.S. selezionato).
  const [filtroAnnoAgev, setFiltroAnnoAgev] = useState(initialAgevFilter === "agev" ? "cur" : "");
  // Modalità "Con Agevolazione": nasconde colonne anagrafiche estese, mostra breakdown categoria
  // (dichiarato PRIMA di filteredAlunni useMemo che lo usa per il filtro "Tutti")
  const isAgevMode = initialAgevFilter === "agev";
  // Mostra colonne DOTE/Agev.Netta e il tasto AGEVOLAZIONE: nella gestione "agevolazioni"
  // (qualsiasi tab) oppure in modalità agevolazione. In "segremat"/"admin" NON si mostrano.
  const showAgevUI = gestione === "agevolazioni" || isAgevMode;
  // Filtro dedicato per Stato dell'anno+1 (feb 2026):
  // "" = nessun filtro, oppure uno dei valori STATI (BOZZA/APPROVATA/NO/USCITO/APPUNTAMENTO)
  const [filtroStatoNext, setFiltroStatoNext] = useState("");
  // Sync con il prop quando React Router riusa l'istanza tra route diverse
  // (es. /alunni ↔ /alunni_agev): se il prop cambia, ripristina il filtro corretto.
  useEffect(() => {
    setFiltroAgev(initialAgevFilter || "");
    setFiltroAnnoAgev(initialAgevFilter === "agev" ? "cur" : "");
  }, [initialAgevFilter]);
  const [doteEdit, setDoteEdit] = useState({}); // { "cod-anno": value editing }
  const [doteSaving, setDoteSaving] = useState({}); // { "cod-anno": bool }
  const [bulkPdfLoading, setBulkPdfLoading] = useState(false);

  // Stampa in blocco delle schede agevolazione dell'anno per gli alunni selezionati.
  // Apre il PDF unico in una nuova tab. Solo per `isAgevMode`.
  const stampaBulkAgevolazioni = useCallback(async () => {
    if (!localAnno) { alert("Seleziona prima un anno scolastico"); return; }
    const cods = Array.from(sel.selected || []).map(Number).filter(Boolean);
    if (cods.length === 0) { alert("Seleziona almeno un alunno"); return; }
    setBulkPdfLoading(true);
    try {
      const r = await api.post(
        "/agevolazioni/bulk-pdf",
        { cods, anno_scol: Number(localAnno), qual: "GENITORE" },
        { responseType: "blob", timeout: 300000 }  // 5 min per lotti grandi
      );
      // Quando responseType=blob axios non parsa eventuali errori JSON: convertili a mano
      if (r.data && r.data.type === "application/json") {
        const txt = await r.data.text();
        let detail = txt;
        try { detail = JSON.parse(txt)?.detail || txt; } catch {}
        throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
      }
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      // Usa un <a> anchor invece di window.open per evitare popup blocker
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.download = `agevolazioni_blocco_${localAnno}_${cods.length}_alunni.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Cleanup URL dopo 60s
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Errore generazione PDF";
      alert(`Errore generazione PDF blocco: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`);
    } finally {
      setBulkPdfLoading(false);
    }
  }, [sel.selected, localAnno]);

  const filteredAlunni = useMemo(() => {
    let base = alunni || [];
    // Filtro per Stato anno+1 (indipendente da filtroAgev)
    if (filtroStatoNext) {
      base = base.filter(a => (agevStatiNext[Number(a.Cod)] || "") === filtroStatoNext);
    }
    if (filtroAgev) {
      base = base.filter((a) => {
        const codNum = Number(a.Cod);
        const isActive = agevActiveSet.has(codNum);
        const isActiveNext = agevActiveSetNext.has(codNum);
        const isActivePrev = agevActiveSetPrev.has(codNum);
        const stato = agevStati[codNum];
        const statoNext = agevStatiNext[codNum];
        const hasDote = (agevDote[codNum] || 0) > 0;
        const hasDoteNext = (agevDoteNext[codNum] || 0) > 0;
        switch (filtroAgev) {
          // "Agevolazioni": include alunni con scheda in almeno uno dei 3 anni
          // (A-1 OR A OR A+1) → permette di vedere tutto lo storico ravvicinato.
          case "agev":      return isActive || isActiveNext || isActivePrev;
          case "dote":      return hasDote || hasDoteNext;
          case "agev_dote": return (isActive && hasDote) || (isActiveNext && hasDoteNext);
          case "approvata": return stato === "APPROVATA" || statoNext === "APPROVATA";
          default: return true;
        }
      });
    }
    // 🆕 Feb 2026: filtro "Anno Agev." — solo alunni con scheda agevolazione per l'anno indicato
    if (filtroAnnoAgev === "prev") {
      base = base.filter(a => agevActiveSetPrev.has(Number(a.Cod)));
    } else if (filtroAnnoAgev === "cur") {
      base = base.filter(a => agevActiveSet.has(Number(a.Cod)));
    } else if (filtroAnnoAgev === "next") {
      base = base.filter(a => agevActiveSetNext.has(Number(a.Cod)));
    } else if (isAgevMode) {
      // Tutti = union globale (qualunque anno) — attivo solo in modalità Agevolazioni
      base = base.filter(a => agevActiveSetAny.has(Number(a.Cod)));
    }
    // 🆕 Feb 2026: ordinamento client-side per "Agev. Netta" (solo modalità agev)
    if (sortMode === "agev_netta_desc" || sortMode === "agev_netta_asc") {
      const dir = sortMode === "agev_netta_desc" ? -1 : 1;
      base = [...base].sort((a, b) => {
        const va = Number(agevNetta[Number(a.Cod)] || 0);
        const vb = Number(agevNetta[Number(b.Cod)] || 0);
        if (va !== vb) return dir * (va - vb);
        // tiebreak: cognome+nome
        const ca = (a.Cog || "").toLowerCase();
        const cb = (b.Cog || "").toLowerCase();
        if (ca !== cb) return ca < cb ? -1 : 1;
        const na = (a.Nom || "").toLowerCase();
        const nb = (b.Nom || "").toLowerCase();
        return na < nb ? -1 : (na > nb ? 1 : 0);
      });
    }
    return base;
  }, [alunni, filtroAgev, filtroStatoNext, filtroAnnoAgev, isAgevMode, agevActiveSet, agevActiveSetNext, agevActiveSetPrev, agevActiveSetAny, agevStati, agevStatiNext, agevDote, agevDoteNext, sortMode, agevNetta]);

  // 🆕 Ago 2026: Export Excel (.xlsx) delle righe attualmente visibili + campi selezionati.
  const exportFields = useMemo(() => [
    { key: "Cod", label: "Codice", get: a => a.Cod },
    { key: "Cog", label: "Cognome", get: a => a.Cog || "" },
    { key: "Nom", label: "Nome", get: a => a.Nom || "" },
    { key: "NasDat", label: "Data nascita", get: a => a.NasDat || "" },
    { key: "NasLuo", label: "Luogo nascita", get: a => a.NasLuo || "" },
    { key: "CodFis", label: "Codice Fiscale", get: a => a.CodFis || "" },
    { key: "settore", label: "Settore", get: a => a.settore || "" },
    { key: "classe", label: "Classe", get: a => a.classe || "" },
    { key: "ciclo", label: "Ciclo", get: a => a.ciclo_curriculum || "" },
    { key: "Tel", label: "Telefono", get: a => a.Tel1 || a.Tel2 || "" },
    { key: "Mai", label: "Email", get: a => a.Mai || "" },
    { key: "ResVia", label: "Indirizzo", get: a => a.ResVia || "" },
    { key: "ResCap", label: "CAP", get: a => a.ResCap || "" },
    { key: "ResLuo", label: "Comune", get: a => a.ResLuo || "" },
    { key: "tot_addebiti", label: "Addebiti €", num: true, get: a => Number(a.tot_addebiti || 0) },
    { key: "tot_non_pagato", label: "Non pagato €", num: true, get: a => Number(a.tot_non_pagato || 0) },
    { key: "tot_non_pagato_scaduto", label: "Scaduto €", num: true, get: a => Number(a.tot_non_pagato_scaduto || 0) },
    { key: "tot_escluso", label: "Escluso €", num: true, get: a => Number(a.tot_escluso || 0) },
    { key: "dote", label: "Dote €", num: true, get: a => Number(agevDote[Number(a.Cod)] || 0) },
    { key: "agev_netta", label: "Agev. Netta €", num: true, get: a => Number(agevNetta[Number(a.Cod)] || 0) },
  ], [agevDote, agevNetta]);

  const defaultExportKeys = useMemo(() => {
    const base = ["Cod", "Cog", "Nom", "settore", "classe"];
    if (!isAgevMode) base.push("NasDat", "CodFis", "Tel");
    if (showTotali) base.push("tot_addebiti", "tot_non_pagato", "tot_non_pagato_scaduto", "tot_escluso");
    if (isAgevMode) base.push("dote", "agev_netta");
    return base;
  }, [isAgevMode, showTotali]);

  // Seed dei campi SOLO la prima volta (nessun salvataggio presente); poi resta all'ultimo setup.
  useEffect(() => {
    if (exportSel === null) {
      const sel = {};
      exportFields.forEach(f => { sel[f.key] = defaultExportKeys.includes(f.key); });
      setExportSel(sel);
    }
  }, [exportSel, exportFields, defaultExportKeys]);
  // Persiste la selezione campi: riaprendo l'export si mantiene l'ultimo setup scelto.
  useEffect(() => {
    if (exportSel) {
      try { localStorage.setItem("alunni_export_fields_v1", JSON.stringify(exportSel)); } catch (e) { /* ignore */ }
    }
  }, [exportSel]);

  // 🆕 Scheda Alunno: carica lo stato del toggle globale (solo segremat, non agev)
  useEffect(() => {
    if (isAgevMode) return;
    let alive = true;
    api.get("/alunni-portal/scheda-config")
      .then(r => { if (alive) setSchedaEnabled(!!r.data?.enabled); })
      .catch(() => {});
    return () => { alive = false; };
  }, [isAgevMode]);

  const toggleScheda = async () => {
    setSchedaSaving(true);
    try {
      const r = await api.put("/alunni-portal/scheda-config", { enabled: !schedaEnabled });
      setSchedaEnabled(!!r.data?.enabled);
    } catch (e) {
      alert("Errore aggiornamento visibilità scheda: " + (e.response?.data?.detail || e.message));
    } finally { setSchedaSaving(false); }
  };


  const openExport = () => setExportOpen(true);

  const doExport = () => {
    const sel = exportSel || {};
    const cols = exportFields.filter(f => sel[f.key]);
    if (cols.length === 0) { alert("Nessun campo selezionato. Apri \"Campi Excel\" e scegli i campi da esportare."); return; }
    const rows = (filteredAlunni || []).map(a => {
      const o = {};
      cols.forEach(c => { o[c.label] = c.get(a); });
      return o;
    });
    const ws = XLSX.utils.json_to_sheet(rows, { header: cols.map(c => c.label) });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Alunni");
    const annoLbl = localAnno ? `${localAnno}-${String((localAnno || 0) + 1).slice(-2)}` : "";
    XLSX.writeFile(wb, `alunni_${annoLbl}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    setExportOpen(false);
  };


  // Modalità "Con Agevolazione": nasconde colonne anagrafiche estese, mostra breakdown categoria
  // (definito sopra prima di filteredAlunni)

  // totalCols dinamico:
  // base (# Cod Cog Nom Set Cla) = 6
  // + se !isAgevMode: Nas, CF, Tel, ✉ = +4
  // + showTotali: Addebiti, N.Pag, Scad, Escl (Dote rimosso come da richiesta utente) = +4
  // + isAgevMode: 4 colonne (ISEE MANC, DICH., ISEE, % CONTRIB) — % Contrib aggiunta feb-2026
  // + 3 (⚙, DOTE, AgevNetta) sempre
  // + isAgevMode anno+1 (rimosso completamente feb-2026)
  // + isAgevMode OK-1 + OK + OK+1 = +3
  // + isAgevMode: +1 Stato (anno corrente)
  const totalCols = 6 + (isAgevMode ? 0 : 4) + (showTotali ? 4 : 0) + (isAgevMode ? 4 : 0) + 3 + 3 + (isAgevMode ? 1 : 0) - (showAgevUI ? 0 : 2);

  // Somma verticale delle agevolazioni nette per le righe attualmente visibili
  const totaleAgevSomma = useMemo(() => {
    const rows = raggruppato ? (alunni || []) : (filteredAlunni || []);
    let s = 0;
    for (const a of rows) {
      const v = agevNetta[Number(a.Cod)];
      if (v) s += Number(v) || 0;
    }
    return s;
  }, [raggruppato, alunni, filteredAlunni, agevNetta]);
  const totaleAgevSommaNext = useMemo(() => {
    const rows = raggruppato ? (alunni || []) : (filteredAlunni || []);
    let s = 0;
    for (const a of rows) {
      const v = agevNettaNext[Number(a.Cod)];
      if (v) s += Number(v) || 0;
    }
    return s;
  }, [raggruppato, alunni, filteredAlunni, agevNettaNext]);
  const requestRef = React.useRef(0);
  const limit = 5000;  // No pagination: tutta la lista sempre caricata (richiesta utente)

  const reloadAgevSet = useCallback(async () => {
    if (!localAnno) { setAgevActiveSet(new Set()); setAgevStati({}); setAgevDote({}); setAgevTot({}); setAgevNetta({}); setAgevVerificata({}); return; }
    try {
      const r = await api.get(`/agevolazioni/active-set?anno=${localAnno}`);
      setAgevActiveSet(new Set((r.data.codes || []).map(Number)));
      const stati = {}, dote = {}, agev = {}, netta = {}, verif = {};
      Object.entries(r.data.stati || {}).forEach(([k, v]) => { stati[Number(k)] = v; });
      Object.entries(r.data.dote || {}).forEach(([k, v]) => { dote[Number(k)] = Number(v) || 0; });
      Object.entries(r.data.agev || {}).forEach(([k, v]) => { agev[Number(k)] = Number(v) || 0; });
      Object.entries(r.data.agev_netta || {}).forEach(([k, v]) => { netta[Number(k)] = Number(v) || 0; });
      Object.entries(r.data.verificata || {}).forEach(([k, v]) => { verif[Number(k)] = !!v; });
      setAgevStati(stati);
      setAgevDote(dote);
      setAgevTot(agev);
      setAgevNetta(netta);
      setAgevVerificata(verif);
    } catch { setAgevActiveSet(new Set()); setAgevStati({}); setAgevDote({}); setAgevTot({}); setAgevNetta({}); setAgevVerificata({}); }
  }, [localAnno]);
  useEffect(() => { reloadAgevSet(); }, [reloadAgevSet]);

  // ANNO+1: carica gli stessi dati per l'anno successivo (solo in isAgevMode)
  const annoNext = localAnno ? Number(localAnno) + 1 : null;
  const reloadAgevSetNext = useCallback(async () => {
    if (!isAgevMode || !annoNext) {
      setAgevActiveSetNext(new Set()); setAgevStatiNext({}); setAgevDoteNext({}); setAgevNettaNext({}); setAgevVerificataNext({}); return;
    }
    try {
      const r = await api.get(`/agevolazioni/active-set?anno=${annoNext}`);
      setAgevActiveSetNext(new Set((r.data.codes || []).map(Number)));
      const stati = {}, dote = {}, netta = {}, verif = {};
      Object.entries(r.data.stati || {}).forEach(([k, v]) => { stati[Number(k)] = v; });
      Object.entries(r.data.dote || {}).forEach(([k, v]) => { dote[Number(k)] = Number(v) || 0; });
      Object.entries(r.data.agev_netta || {}).forEach(([k, v]) => { netta[Number(k)] = Number(v) || 0; });
      Object.entries(r.data.verificata || {}).forEach(([k, v]) => { verif[Number(k)] = !!v; });
      setAgevStatiNext(stati);
      setAgevDoteNext(dote);
      setAgevNettaNext(netta);
      setAgevVerificataNext(verif);
    } catch {
      setAgevActiveSetNext(new Set()); setAgevStatiNext({}); setAgevDoteNext({}); setAgevNettaNext({}); setAgevVerificataNext({});
    }
  }, [isAgevMode, annoNext]);
  useEffect(() => { reloadAgevSetNext(); }, [reloadAgevSetNext]);

  // 🆕 Feb 2026: ANNO−1 (solo Verificata/OK, per la colonna anno precedente in isAgevMode).
  const annoPrev = localAnno ? Number(localAnno) - 1 : null;
  const reloadAgevSetPrev = useCallback(async () => {
    if (!isAgevMode || !annoPrev) {
      setAgevActiveSetPrev(new Set()); setAgevVerificataPrev({}); return;
    }
    try {
      const r = await api.get(`/agevolazioni/active-set?anno=${annoPrev}`);
      setAgevActiveSetPrev(new Set((r.data.codes || []).map(Number)));
      const verif = {};
      Object.entries(r.data.verificata || {}).forEach(([k, v]) => { verif[Number(k)] = !!v; });
      setAgevVerificataPrev(verif);
    } catch {
      setAgevActiveSetPrev(new Set()); setAgevVerificataPrev({});
    }
  }, [isAgevMode, annoPrev]);
  useEffect(() => { reloadAgevSetPrev(); }, [reloadAgevSetPrev]);

  // 🆕 Feb 2026: caricamento Set globale (tutti gli anni) — usato per "Tutti" in Agev.
  const reloadAgevSetAny = useCallback(async () => {
    if (!isAgevMode) { setAgevActiveSetAny(new Set()); return; }
    try {
      const r = await api.get(`/agevolazioni/active-set-any`);
      setAgevActiveSetAny(new Set((r.data.codes || []).map(Number)));
    } catch {
      setAgevActiveSetAny(new Set());
    }
  }, [isAgevMode]);
  useEffect(() => { reloadAgevSetAny(); }, [reloadAgevSetAny]);

  // Toggle del flag "verificata" (OK) per (cod, anno). Aggiorna ottimisticamente.
  // Se la scheda non esiste, il backend la crea automaticamente come BOZZA.
  const toggleVerificata = useCallback(async (cod, year) => {
    const isNext = annoNext && Number(year) === Number(annoNext);
    const isPrev = annoPrev && Number(year) === Number(annoPrev);
    const cur = isNext ? !!agevVerificataNext[Number(cod)]
              : isPrev ? !!agevVerificataPrev[Number(cod)]
              : !!agevVerificata[Number(cod)];
    const next = !cur;
    // optimistic update
    if (isNext) setAgevVerificataNext(prev => ({ ...prev, [Number(cod)]: next }));
    else if (isPrev) setAgevVerificataPrev(prev => ({ ...prev, [Number(cod)]: next }));
    else setAgevVerificata(prev => ({ ...prev, [Number(cod)]: next }));
    try {
      const r = await api.post(`/agevolazioni/${Number(cod)}/${Number(year)}/verificata`, { verificata: next });
      // Se è stata creata una nuova scheda, ricarica gli active-set per aggiornare i bottoni ⚙
      if (r.data?.created) {
        if (isNext) await reloadAgevSetNext();
        else if (isPrev) await reloadAgevSetPrev();
        else await reloadAgevSet();
      }
    } catch (e) {
      // rollback
      if (isNext) setAgevVerificataNext(prev => ({ ...prev, [Number(cod)]: cur }));
      else if (isPrev) setAgevVerificataPrev(prev => ({ ...prev, [Number(cod)]: cur }));
      else setAgevVerificata(prev => ({ ...prev, [Number(cod)]: cur }));
      alert(e.response?.data?.detail || "Errore aggiornamento flag verificata");
    }
  }, [agevVerificata, agevVerificataNext, agevVerificataPrev, annoNext, annoPrev, reloadAgevSet, reloadAgevSetNext, reloadAgevSetPrev]);

  // Toggle del flag "isee_mancante" per l'anno selezionato dal filtro Anno ISEE.
  // Auto-create scheda se manca.
  const toggleIseeMancante = useCallback(async (cod) => {
    const year = iseeFilterAnno;
    if (!year) return;
    const cur = !!iseeMancanteFiltered[Number(cod)];
    const next = !cur;
    // optimistic update
    setIseeMancanteFiltered(prev => ({ ...prev, [Number(cod)]: next }));
    try {
      const r = await api.post(`/agevolazioni/${Number(cod)}/${Number(year)}/isee-mancante`, { isee_mancante: next });
      if (r.data?.created) {
        // Se la scheda è stata creata per l'anno corrente o l'anno+1, ricarica le mappe relative
        if (Number(year) === Number(localAnno)) await reloadAgevSet();
        else if (Number(year) === Number(annoNext)) await reloadAgevSetNext();
      }
    } catch (e) {
      // rollback
      setIseeMancanteFiltered(prev => ({ ...prev, [Number(cod)]: cur }));
      alert(e.response?.data?.detail || "Errore aggiornamento flag ISEE mancante");
    }
  }, [iseeMancanteFiltered, iseeFilterAnno, localAnno, annoNext, reloadAgevSet, reloadAgevSetNext]);

  // Toggle dei flag "dichiarazione_inviata" / "dichiarazione_firmata" per
  // l'anno selezionato dal filtro Anno ISEE. Auto-create scheda se manca.
  const toggleDichiarazione = useCallback(async (cod, flag /* "inviata" | "firmata" */) => {
    const year = iseeFilterAnno;
    if (!year) return;
    const setter = flag === "inviata" ? setDichInviataFiltered : setDichFirmataFiltered;
    const curMap = flag === "inviata" ? dichInviataFiltered : dichFirmataFiltered;
    const cur = !!curMap[Number(cod)];
    const next = !cur;
    setter(prev => ({ ...prev, [Number(cod)]: next }));
    try {
      await api.post(`/agevolazioni/${Number(cod)}/${Number(year)}/dichiarazione`, { [flag]: next });
    } catch (e) {
      setter(prev => ({ ...prev, [Number(cod)]: cur }));
      alert(e.response?.data?.detail || `Errore aggiornamento flag dichiarazione ${flag}`);
    }
  }, [dichInviataFiltered, dichFirmataFiltered, iseeFilterAnno]);

  // Carica la tabella ISEE per l'anno scelto nel filtro "Anno ISEE" (solo in modalità Agevolazioni)
  useEffect(() => {
    if (!isAgevMode || !iseeFilterAnno) { setIseeMap({}); return; }
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/mensa-mese/isee/list?anno=${iseeFilterAnno}`);
        if (!alive) return;
        const m = {};
        for (const it of (r.data?.data || [])) {
          const cf = String(it.cod_fis || "").toUpperCase().trim();
          if (cf) m[cf] = Number(it.importo) || 0;
        }
        setIseeMap(m);
      } catch { if (alive) setIseeMap({}); }
    })();
    return () => { alive = false; };
  }, [isAgevMode, iseeFilterAnno]);

  // Carica la mappa "ISEE Mancante" per l'anno selezionato dal filtro
  useEffect(() => {
    if (!isAgevMode || !iseeFilterAnno) {
      setIseeMancanteFiltered({});
      setDichInviataFiltered({});
      setDichFirmataFiltered({});
      return;
    }
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/agevolazioni/active-set?anno=${iseeFilterAnno}`);
        if (!alive) return;
        const miss = {}; const inv = {}; const fir = {};
        Object.entries(r.data.isee_mancante || {}).forEach(([k, v]) => { miss[Number(k)] = !!v; });
        Object.entries(r.data.dichiarazione_inviata || {}).forEach(([k, v]) => { inv[Number(k)] = !!v; });
        Object.entries(r.data.dichiarazione_firmata || {}).forEach(([k, v]) => { fir[Number(k)] = !!v; });
        setIseeMancanteFiltered(miss);
        setDichInviataFiltered(inv);
        setDichFirmataFiltered(fir);
      } catch { if (alive) { setIseeMancanteFiltered({}); setDichInviataFiltered({}); setDichFirmataFiltered({}); } }
    })();
    return () => { alive = false; };
  }, [isAgevMode, iseeFilterAnno]);

  // Cambia stato BOZZA <-> CONFERMATA (alias UI di APPROVATA)
  // Se `anno` è passato e diverso da localAnno aggiorna le mappe "Next".
  const cambiaStato = async (cod, newStato, anno = null) => {
    const targetAnno = anno || localAnno;
    const isNext = !!anno && Number(anno) !== Number(localAnno);
    const backendStato = newStato === "CONFERMATA" ? "APPROVATA" : newStato;
    try {
      await api.post(`/agevolazioni/${cod}/${targetAnno}/stato`, { stato: backendStato });
      if (isNext) setAgevStatiNext((m) => ({ ...m, [Number(cod)]: backendStato }));
      else setAgevStati((m) => ({ ...m, [Number(cod)]: backendStato }));
    } catch (e) {
      alert("⚠ Errore cambio stato: " + (e.response?.data?.detail || e.message));
    }
  };

  const saveDote = useCallback(async (cod, anno, value) => {
    const k = `${cod}-${anno}`;
    setDoteSaving((s) => ({ ...s, [k]: true }));
    const isNext = Number(anno) !== Number(localAnno);
    try {
      await api.post(`/agevolazioni/${cod}/${anno}/dote`, { dote_scuola: Number(value) || 0 });
      // aggiorna localmente nelle mappe corrette (corrente vs anno+1)
      if (isNext) {
        setAgevDoteNext((d) => ({ ...d, [Number(cod)]: Number(value) || 0 }));
        setAgevActiveSetNext((s) => { const n = new Set(s); n.add(Number(cod)); return n; });
        setAgevStatiNext((m) => ({ ...m, [Number(cod)]: m[Number(cod)] || "BOZZA" }));
      } else {
        setAgevDote((d) => ({ ...d, [Number(cod)]: Number(value) || 0 }));
        setAgevActiveSet((s) => { const n = new Set(s); n.add(Number(cod)); return n; });
        setAgevStati((m) => ({ ...m, [Number(cod)]: m[Number(cod)] || "BOZZA" }));
      }
      setDoteEdit((m) => { const n = { ...m }; delete n[k]; return n; });
    } catch (e) {
      alert(e.response?.data?.detail || "Errore salvataggio dote");
    } finally {
      setDoteSaving((s) => { const n = { ...s }; delete n[k]; return n; });
    }
  }, [localAnno]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getAnniDisponibili();
        const anni = res.data.anni || [];
        setAnniList(anni);
        setLocalAnno(anni.includes(annoCorrente) ? annoCorrente : (anni[0] || null));
      } catch (e) { console.error(e); }
    })();
  }, [annoCorrente]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getFiltriClassi(localAnno);
        setSettoriList(res.data.settori || []);
        // Preferisce classi_dettaglio (oggetti con nome+label+settore) per evitare
        // match-issue: il backend cerca su ClaNom puro, mentre il label include " (S)".
        setClassiList(res.data.classi_dettaglio || res.data.classi || []);
      } catch (e) { console.error(e); }
    })();
    setSettoreFilter([]); setCicloFilter(""); setClasseFilter([]);
  }, [localAnno]);

  const fetchData = useCallback(async () => {
    const reqId = ++requestRef.current;
    setLoading(true);
    try {
      const params = { page: raggruppato ? 1 : page, limit };
      if (localAnno) params.anno = localAnno;
      if (search) params.search = search;
      if (settoreFilter.length) params.settore = settoreFilter.join(",");
      if (cicloFilter) params.ciclo = cicloFilter;
      if (classeFilter.length) params.classe_nome = classeFilter.join(",");
      if (!raggruppato && sortBy) {
        // L'ordinamento per colonna finanziaria (click su intestazione) ha priorità
        params.sort_by = sortBy;
        params.sort_dir = sortDir;
      } else if (sortMode && sortMode !== "cog" && !sortMode.startsWith("agev_netta_")) {
        // Ordinamento da selettore (settore_classe / settore_cog). "cog" usa il default backend.
        // "agev_netta_*" è gestito client-side su filteredAlunni (il backend non lo conosce).
        params.sort_by = sortMode;
      }
      const res = await getAlunni(params);
      if (reqId !== requestRef.current) return;
      setAlunni(res.data.data); setTotal(res.data.total);
    } catch (e) { console.error(e); }
    finally { if (reqId === requestRef.current) setLoading(false); }
  }, [localAnno, page, search, settoreFilter, cicloFilter, classeFilter, sortBy, sortDir, sortMode, limit, raggruppato]);

  useEffect(() => { setPage(1); }, [search, localAnno, settoreFilter, cicloFilter, classeFilter, sortBy, sortDir, sortMode, raggruppato]);
  useEffect(() => { fetchData(); }, [fetchData]);

  // 🆕 Batch fetch counts iscrizione (Cau=1) per gli alunni visibili
  useEffect(() => {
    const cods = (alunni || []).map((a) => Number(a.Cod)).filter(Boolean);
    if (!cods.length || !localAnno) { setIscrCounts({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post("/alunni/iscrizione-movimenti/counts", {
          cods, anno: Number(localAnno),
        });
        if (!cancelled) setIscrCounts(r.data?.counts || {});
      } catch (e) {
        console.warn("iscr-counts fetch failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, [alunni, localAnno]);

  // 🆕 Apre modal con lista movimenti Cau=1 di un singolo alunno
  const openIscrizioneModal = useCallback(async (cod) => {
    try {
      const r = await api.get(`/alunni/${cod}/iscrizione-movimenti`, { params: { anno: Number(localAnno) } });
      const resp = r.data || {};
      setIscrModal({
        iscr: {
          alunno_cod: resp.alunno_cod, cognome: resp.cognome, nome: resp.nome, anno: resp.anno,
        },
        data: resp,
      });
    } catch (e) {
      alert("Errore caricamento movimenti iscrizione: " + (e.response?.data?.detail || e.message));
    }
  }, [localAnno]);

  // Richiesta utente: il click sulla tab Alunni deve sempre ripristinare la lista completa.
  // Quando l'utente naviga di nuovo alla pagina (location.key cambia), resettiamo i filtri.
  // Skippa il primo mount.
  const firstMountRef = React.useRef(true);
  useEffect(() => {
    if (firstMountRef.current) { firstMountRef.current = false; return; }
    setSearch("");
    setSettoreFilter([]);
    setCicloFilter("");
    setClasseFilter([]);
    setSortBy(null);
    setSortMode(initialAgevFilter === "agev" ? "cog" : "settore_classe");
    setRaggruppato(initialAgevFilter !== "agev");
    setPage(1);
  }, [location.key]);

  // Conteggi per tab settore (non dipende da settoreFilter, dipende da anno/search)
  // In isAgevMode i contatori vengono RIDOTTI alla sola popolazione con scheda agev
  // (anno corrente OR anno+1) — i totalizzatori devono rispecchiare la lista filtrata
  // di "Agevolazioni", non quella globale (richiesta utente feb-2026).
  useEffect(() => {
    if (!localAnno) { setSettoriCounts({}); setTotalAllSettori(0); setCicloCounts({}); setTotalAllCicli(0); return; }
    (async () => {
      try {
        const r = await getAlunniCountsBySettore({ anno: localAnno, ...(search ? { search } : {}) });
        setSettoriCounts(r.data?.counts || {});
        setTotalAllSettori(r.data?.total || 0);
      } catch (e) { console.error(e); }
      try {
        const r2 = await getAlunniCountsByCiclo({ anno: localAnno, ...(search ? { search } : {}) });
        setCicloCounts(r2.data?.counts || {});
        setTotalAllCicli(r2.data?.total || 0);
      } catch (e) { console.error(e); }
    })();
  }, [localAnno, search]);

  // 🆕 Dashboard totali addebiti: si aggiorna al cambio di anno/settore/search
  // Mostrata solo nella modalità Segremat (showTotali) e non in isAgevMode.
  useEffect(() => {
    if (!showTotali || !localAnno) { setDashAddeb(null); setDashSerie([]); return; }
    let cancelled = false;
    setDashAddebLoading(true);
    (async () => {
      try {
        const p = {
          anno: localAnno,
          ...(settoreFilter.length === 1 ? { settore: settoreFilter[0] } : {}),
          ...(search ? { search } : {}),
        };
        const [r, rSerie] = await Promise.all([
          getAlunniDashboardAddebiti(p),
          getAlunniDashboardAddebitiSerie(p),
        ]);
        if (!cancelled) {
          setDashAddeb(r.data || null);
          setDashSerie(rSerie.data?.serie || []);
        }
      } catch (e) {
        if (!cancelled) { setDashAddeb(null); setDashSerie([]); }
        console.error(e);
      } finally {
        if (!cancelled) setDashAddebLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showTotali, localAnno, settoreFilter, search]);

  // Quando isAgevMode è attivo, sovrascriviamo i conteggi server-side con quelli
  // calcolati sulla lista alunni filtrata (solo con scheda agevolazione attiva).
  // 🆕 Feb 2026: rispetta il filtro "Anno Agev." per mostrare gli stessi alunni
  // che appariranno in tabella (coerenza con Comitato Solidale).
  const agevCounts = useMemo(() => {
    if (!isAgevMode) return null;
    const settoriC = {};
    const cicloC = {};
    let total = 0;
    for (const a of (alunni || [])) {
      const codNum = Number(a.Cod);
      let hasAgev;
      if (filtroAnnoAgev === "prev") hasAgev = agevActiveSetPrev.has(codNum);
      else if (filtroAnnoAgev === "cur") hasAgev = agevActiveSet.has(codNum);
      else if (filtroAnnoAgev === "next") hasAgev = agevActiveSetNext.has(codNum);
      else hasAgev = agevActiveSetAny.has(codNum); // Tutti
      if (!hasAgev) continue;
      total += 1;
      const s = (a.settore || "").split(",")[0].trim().toUpperCase();
      if (s) settoriC[s] = (settoriC[s] || 0) + 1;
      const ci = (a.ciclo_curriculum || "").toLowerCase();
      if (ci) cicloC[ci] = (cicloC[ci] || 0) + 1;
    }
    return { settoriCounts: settoriC, cicloCounts: cicloC, total };
  }, [isAgevMode, alunni, filtroAnnoAgev, agevActiveSet, agevActiveSetNext, agevActiveSetPrev, agevActiveSetAny]);

  // Effetto di override: appena `agevCounts` cambia, applico le mappe
  useEffect(() => {
    if (!agevCounts) return;
    setSettoriCounts(agevCounts.settoriCounts);
    setTotalAllSettori(agevCounts.total);
    setCicloCounts(agevCounts.cicloCounts);
    setTotalAllCicli(agevCounts.total);
  }, [agevCounts]);

  const toggleSort = (col) => {
    // Se l'utente ordina per una colonna numerica, disattiviamo il raggruppamento
    // così la lista è piatta e si vede l'ordine richiesto
    if (raggruppato) setRaggruppato(false);
    if (sortBy === col) { if (sortDir === "desc") setSortDir("asc"); else { setSortBy(null); setSortDir("desc"); } }
    else { setSortBy(col); setSortDir("desc"); }
  };
  const sortArrow = (col) => {
    if (sortBy !== col) return " ↕";
    return sortDir === "desc" ? " ↓" : " ↑";
  };

  const totalPages = Math.ceil(total / limit);

  // Etichette settori brevi (in linea con counts-by-settore)
  // A=INFANZIA, E+F=PRIMARIA, M=SECONDARIA (richiesta utente)
  const SET_LABEL = { A: "INFANZIA", E: "PRIMARIA", F: "PRIMARIA", M: "SECONDARIA" };
  // Ordinamento canonico settori
  const SET_ORDER = { A: 1, E: 2, F: 3, M: 4 };
  const settoreSortKey = (s) => SET_ORDER[(s || "").toUpperCase()] || 99;

  // Calcolo gruppi: lista piatta {kind:'header'|'row'|'subtotal', ...}
  const groupedRows = useMemo(() => {
    if (!raggruppato) return null;
    // Quando l'ordinamento è puramente alfabetico, niente headers di gruppo
    if (sortMode === "cog") return null;
    const arr = [...filteredAlunni];
    const groupByClasse = sortMode !== "settore_cog";
    arr.sort((a, b) => {
      const sa = (a.settore || "").split(",")[0].trim().toUpperCase();
      const sb = (b.settore || "").split(",")[0].trim().toUpperCase();
      const ka = settoreSortKey(sa), kb = settoreSortKey(sb);
      if (ka !== kb) return ka - kb;
      if (sa !== sb) return sa.localeCompare(sb);
      if (groupByClasse) {
        const ca = (a.classe || "").split(",")[0].trim();
        const cb = (b.classe || "").split(",")[0].trim();
        if (ca !== cb) return ca.localeCompare(cb, "it", { numeric: true });
      }
      const cga = (a.Cog || "").toUpperCase();
      const cgb = (b.Cog || "").toUpperCase();
      if (cga !== cgb) return cga.localeCompare(cgb, "it");
      return (a.Nom || "").localeCompare(b.Nom || "", "it");
    });
    const out = [];
    let curKey = "_INIT_";
    let curSet = "";
    let curCls = "";
    let curCount = 0;
    let groupIdx = 0;
    for (const a of arr) {
      const set = (a.settore || "").split(",")[0].trim().toUpperCase() || "_";
      const cls = (a.classe || "").split(",")[0].trim() || "—";
      const key = groupByClasse ? `${set}__${cls}` : set;
      if (key !== curKey) {
        if (curKey !== "_INIT_") {
          out.push({ kind: "subtotal", settore: curSet, classe: curCls, count: curCount, groupByClasse });
        }
        out.push({ kind: "header", settore: set, settoreLabel: SET_LABEL[set] || set, classe: cls, gid: ++groupIdx, groupByClasse });
        curKey = key; curSet = set; curCls = cls; curCount = 0;
      }
      out.push({ kind: "row", data: a, indexInGroup: ++curCount });
    }
    if (curKey !== "_INIT_") {
      out.push({ kind: "subtotal", settore: curSet, classe: curCls, count: curCount, groupByClasse });
    }
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredAlunni, raggruppato, sortMode]);

  return (
    <div data-testid="alunni-page">
      <div className="sw-page-title">
        <h1>{titlePrefix ? `${titlePrefix} Alunni` : "Alunni"}</h1>
        <div className="sw-breadcrumb">Home &gt; {titlePrefix ? `${titlePrefix} Alunni` : "Alunni"}</div>
        {!isAgevMode && (
          <div data-testid="scheda-visibilita-box" title="Attiva/disattiva la visibilità della Scheda Alunno nel Portale Alunni (gli alunni vedono la propria scheda via login Gmail)"
            style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 10, background: "white", border: "1px solid #e3e8ef", borderRadius: 8, padding: "6px 12px" }}>
            <div style={{ lineHeight: 1.15 }}>
              <div style={{ fontWeight: 800, fontSize: 12, color: "#0f172a" }}>Visibilità nel Portale Alunni</div>
              <div style={{ fontSize: 10, color: "#64748b" }}>{schedaEnabled ? "Attiva: gli alunni vedono la propria scheda." : "Non attiva: la scheda è nascosta nel portale."}</div>
            </div>
            <button data-testid="scheda-visibilita-toggle" onClick={toggleScheda} disabled={schedaSaving}
              style={{ position: "relative", width: 56, height: 28, borderRadius: 999, border: "none", cursor: schedaSaving ? "wait" : "pointer",
                background: schedaEnabled ? "#16a34a" : "#cbd5e1", transition: "background .2s", flexShrink: 0 }}>
              <span style={{ position: "absolute", top: 3, left: schedaEnabled ? 31 : 3, width: 22, height: 22, borderRadius: "50%", background: "white", transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
            </button>
          </div>
        )}
        {isAgevMode && (
          <button onClick={() => navigate(`/g/${gestione}/scuola_agev_report`)}
            data-testid="alunni-agev-report-btn"
            title="Apri Report Agevolazioni"
            style={{ marginLeft: "auto", padding: "7px 14px", background: "#1b5e20", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
            📊 Report Agevolazioni
          </button>
        )}
        {isAgevMode && (
          <button onClick={stampaBulkAgevolazioni} data-testid="alunni-agev-bulk-pdf-btn"
            disabled={sel.size === 0 || bulkPdfLoading}
            title={sel.size === 0 ? "Seleziona almeno un alunno per stampare le schede in blocco" : `Stampa in PDF unico le schede agevolazione dell'anno per i ${sel.size} alunni selezionati`}
            style={{ marginLeft: 8, padding: "7px 14px", background: sel.size === 0 ? "#bdbdbd" : "#0d47a1", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: sel.size === 0 ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
            {bulkPdfLoading ? "⏳ Genero PDF…" : `🖨 Stampa Blocco (${sel.size})`}
          </button>
        )}
        {isAgevMode && (
          <button onClick={() => setShowConfigAgev(true)} data-testid="alunni-agev-config-btn"
            title="Apri il Configuratore Agevolazione Standard (foglio editabile con formule)"
            style={{ marginLeft: 8, padding: "7px 14px", background: "#6a1b9a", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
            ⚙ Configuratore Agevolazione Standard
          </button>
        )}
      </div>
      {showConfigAgev && <ConfiguratoreAgevStandard anno={localAnno || annoCorrente} onClose={() => setShowConfigAgev(false)} />}
      {genitoriAlunno && <GenitoriAlunnoModal alunno={genitoriAlunno} onClose={() => setGenitoriAlunno(null)} />}

      {/* Filters */}
      <div className="sw-filter-box">
        <div className="sw-filter-row">
          <AnnoSelector testId="alunni-anno-attivo" />
          <span className="sw-filter-label" style={{ marginLeft: 8 }}>settore:</span>
          <MultiCheckDropdown
            testId="settore-filter"
            placeholder="Qualsiasi settore"
            minWidth={150}
            options={[...settoriList.map(s => ({ value: s, label: s })), { value: "_ALTRO_", label: "Altro (no A/E/F/M)" }]}
            selected={settoreFilter}
            onToggle={(v) => setSettoreFilter(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])}
            onClear={() => setSettoreFilter([])}
          />
          <span className="sw-filter-label">classe:</span>
          <MultiCheckDropdown
            testId="classe-filter"
            placeholder="Qualsiasi classe"
            minWidth={160}
            options={classiList.map(c => {
              const label = typeof c === "string" ? c : c.label;
              return { value: label, label };
            })}
            selected={classeFilter}
            onToggle={(v) => setClasseFilter(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])}
            onClear={() => setClasseFilter([])}
          />
          <span className="sw-filter-label">cerca:</span>
          <input data-testid="search-alunni" type="text" placeholder="cognome, nome, CF..." value={search} onChange={e=>setSearch(e.target.value)} className="sw-filter-input" style={{width:200}} />
          <span className="sw-filter-label" style={{ marginLeft: 8 }}>ordina:</span>
          <select data-testid="sort-mode" value={sortMode} onChange={(e) => setSortMode(e.target.value)}
                  title="Ordinamento principale lista alunni"
                  className="sw-filter-select" style={{ minWidth: 220 }}>
            <option value="settore_classe">Settore → Classe → Cogn. → Nome</option>
            <option value="settore_cog">Settore → Cogn. → Nome</option>
            <option value="cog">Cogn. → Nome</option>
            {isAgevMode && <option value="agev_netta_desc">💰 Agev. Netta (↓ decrescente)</option>}
            {isAgevMode && <option value="agev_netta_asc">💰 Agev. Netta (↑ crescente)</option>}
          </select>
          {(settoreFilter.length||cicloFilter||classeFilter.length||search) && <button className="sw-filter-btn" onClick={()=>{setSettoreFilter([]);setCicloFilter("");setClasseFilter([]);setSearch("");}}>Pulisci</button>}
          {isAgevMode && (
            <>
              <span className="sw-filter-label" style={{ marginLeft: 8, color: "#4527a0", fontWeight: 700 }}>🗓 Anno ISEE:</span>
              <select data-testid="filtro-anno-isee" value={iseeFilterAnno}
                onChange={e => setIseeFilterAnno(Number(e.target.value))}
                title="Anno ISEE da mostrare nella colonna ISEE e nel flag MANC."
                className="sw-filter-select"
                style={{ minWidth: 90, fontWeight: 700, color: "#4527a0", background: "#ede7f6" }}>
                {(() => {
                  const base = Number(annoCorrente) || new Date().getFullYear();
                  const opts = [];
                  for (let y = base - 3; y <= base + 1; y++) opts.push(y);
                  return opts.map(y => <option key={y} value={y}>{y}</option>);
                })()}
              </select>
              <span className="sw-filter-label" style={{ marginLeft: 8 }}>Anno Agev.:</span>
              <select data-testid="filtro-anno-agev" value={filtroAnnoAgev} onChange={e => setFiltroAnnoAgev(e.target.value)}
                title="Filtra alunni per anno scolastico dell'agevolazione (Tutti = qualsiasi anno)"
                className="sw-filter-select"
                style={{ minWidth: 130, background: filtroAnnoAgev ? "#fff59d" : "#e8f5e9" }}>
                <option value="">Tutti</option>
                <option value="prev">{annoPrev ? `${annoPrev}/${String((annoPrev||0)+1).slice(-2)}` : "A−1"}</option>
                <option value="cur">{localAnno ? `${localAnno}/${String((localAnno||0)+1).slice(-2)}` : "A"}</option>
                <option value="next">{annoNext ? `${annoNext}/${String((annoNext||0)+1).slice(-2)}` : "A+1"}</option>
              </select>
              <span className="sw-filter-label" style={{ marginLeft: 8 }}>Stato +1:</span>
              <select data-testid="filtro-stato-next" value={filtroStatoNext} onChange={e => setFiltroStatoNext(e.target.value)}
                title="Filtra solo alunni con questo Stato nell'anno successivo (+1)"
                className="sw-filter-select"
                style={{ minWidth: 150, background: filtroStatoNext ? "#fff59d" : "white" }}>
                <option value="">— tutti —</option>
                <option value="BOZZA">BOZZA</option>
                <option value="APPROVATA">CONFERMATA</option>
                <option value="NO">NO</option>
                <option value="USCITO">USCITO</option>
                <option value="APPUNTAMENTO">APPUNT.</option>
                <option value="RATE">RATE</option>
                <option value="SC_FRATELLI">SC. FRATELLI</option>
                <option value="FIRMATA">FIRMATA</option>
              </select>
            </>
          )}
          <button data-testid="export-fields-btn" onClick={openExport}
            title="Scegli quali campi includere nell'export Excel (resta salvato)"
            style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, background: "white", color: "#1d6f42", border: "1px solid #1d6f42", padding: "6px 12px", borderRadius: 4, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            Campi Excel
          </button>
          <button data-testid="export-excel-btn" onClick={doExport}
            title="Esporta in Excel le righe attualmente visibili con i campi selezionati"
            style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#1d6f42", color: "white", border: "none", padding: "6px 14px", borderRadius: 4, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            Esporta Excel
          </button>
          <label data-testid="raggruppato-toggle" style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: "auto", fontSize: 11, color: "#444", cursor: "pointer", whiteSpace: "nowrap" }}>
            <input type="checkbox" checked={raggruppato} onChange={e => setRaggruppato(e.target.checked)} />
            Raggruppa per classe
          </label>
        </div>
      </div>

      {exportOpen && (
        <div data-testid="export-modal" onClick={() => setExportOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: "white", borderRadius: 8, width: 520, maxWidth: "92vw", maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 10px 40px rgba(0,0,0,0.3)" }}>
            <div style={{ padding: "14px 18px", background: "#1d6f42", color: "white", fontWeight: 700, fontSize: 15 }}>
              Campi Excel — seleziona i campi da esportare
            </div>
            <div style={{ padding: "8px 18px", fontSize: 12, color: "#555", borderBottom: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <span data-testid="export-rowcount">La selezione resta salvata per i prossimi export</span>
              <span style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setExportSel(Object.fromEntries(exportFields.map(f => [f.key, true])))} style={{ background: "none", border: "none", color: "#1565c0", cursor: "pointer", fontWeight: 700, fontSize: 12 }}>Tutti</button>
                <button onClick={() => setExportSel(Object.fromEntries(exportFields.map(f => [f.key, false])))} style={{ background: "none", border: "none", color: "#c62828", cursor: "pointer", fontWeight: 700, fontSize: 12 }}>Nessuno</button>
                <button onClick={() => setExportSel(Object.fromEntries(exportFields.map(f => [f.key, defaultExportKeys.includes(f.key)])))} style={{ background: "none", border: "none", color: "#555", cursor: "pointer", fontWeight: 700, fontSize: 12 }}>Predefiniti</button>
              </span>
            </div>
            <div style={{ padding: "12px 18px", overflowY: "auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 18px" }}>
              {exportFields.map(f => (
                <label key={f.key} data-testid={`export-field-${f.key}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!(exportSel || {})[f.key]} onChange={() => setExportSel(prev => ({ ...(prev || {}), [f.key]: !(prev || {})[f.key] }))} />
                  <span>{f.label}</span>
                </label>
              ))}
            </div>
            <div style={{ padding: "12px 18px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button data-testid="export-close-btn" onClick={() => setExportOpen(false)} style={{ padding: "8px 18px", border: "none", background: "#1d6f42", color: "white", borderRadius: 4, cursor: "pointer", fontSize: 13, fontWeight: 700 }}>Chiudi</button>
            </div>
          </div>
        </div>
      )}


      {/* Tab settore (Segremat - sincronizzato) */}
      {localAnno && (
        <div style={{ margin: "0 0 8px", display: "flex", gap: 6, flexWrap: "wrap", alignItems: "stretch", borderBottom: "3px solid #1565c0", paddingBottom: 0 }} data-testid="alunni-settore-tabs">
          {(() => {
            const COLORS = {
              "":         { c: "#37474f", bg: "#eceff1" },
              A:          { c: "#e91e63", bg: "#fce4ec" },
              E:          { c: "#2e7d32", bg: "#e8f5e9" },
              F:          { c: "#558b2f", bg: "#f1f8e9" },
              M:          { c: "#1565c0", bg: "#e3f2fd" },
              _ALTRO_:    { c: "#6a1b9a", bg: "#f3e5f5" },
            };
            const DIDATTICI = ["A", "E", "F", "M"];
            const altroCount = settoriCounts["_ALTRO_"] != null
              ? settoriCounts["_ALTRO_"]
              : Object.entries(settoriCounts)
                  .filter(([k]) => !DIDATTICI.includes(k) && k !== "_NONE_" && k !== "_ALTRO_")
                  .reduce((acc, [, v]) => acc + (v || 0), 0);
            const tabs = [
              { k: "",        l: "Tutti",            count: totalAllSettori },
              { k: "A",       l: "A · Infanzia",     count: settoriCounts["A"] || 0 },
              { k: "E",       l: "E · Primaria E",   count: settoriCounts["E"] || 0 },
              { k: "F",       l: "F · Primaria F",   count: settoriCounts["F"] || 0 },
              { k: "M",       l: "M · Secondaria",   count: settoriCounts["M"] || 0 },
              { k: "_ALTRO_", l: "Altro",            count: altroCount },
            ];
            return tabs.map(t => {
              const active = t.k === "" ? settoreFilter.length === 0 : settoreFilter.includes(t.k);
              const col = COLORS[t.k] || COLORS[""];
              return (
                <button key={t.k || "all"} onClick={() => t.k === "" ? setSettoreFilter([]) : setSettoreFilter(prev => prev.includes(t.k) ? prev.filter(x => x !== t.k) : [...prev, t.k])} data-testid={`alunni-tab-settore-${t.k || "all"}`}
                  title={t.k === "_ALTRO_" ? "Settori non didattici (N, S, e altri)" : undefined}
                  style={{
                    padding: "9px 18px",
                    background: active ? col.c : col.bg,
                    color: active ? "white" : col.c,
                    border: `1px solid ${col.c}`,
                    borderBottom: active ? `3px solid ${col.c}` : "1px solid transparent",
                    fontSize: 13, fontWeight: 800, letterSpacing: 0.3,
                    cursor: "pointer", textTransform: "uppercase",
                    display: "flex", alignItems: "center", gap: 8,
                    marginBottom: -3, borderTopWidth: 3,
                  }}>
                  <span>{t.l}</span>
                  <span style={{
                    padding: "1px 8px",
                    background: active ? "rgba(255,255,255,0.25)" : col.c,
                    color: "white", fontSize: 11, fontWeight: 800,
                    minWidth: 26, textAlign: "center", borderRadius: 2,
                  }}>{t.count}</span>
                </button>
              );
            });
          })()}
        </div>
      )}

      {/* 🗑 Feb 2026: Dashboard totali addebiti + grafico evoluzione mensile RIMOSSI
          da Segremat Web Pro su richiesta utente. La stessa info è disponibile
          nella pagina Analytics dedicata. */}

      {/* Send bar deep-link verso Comunicazioni */}
      <AlunniSendBar selected={sel.selected} onClear={sel.clear}
        label={`Alunni ${localAnno ? `${localAnno}/${String((localAnno||0)+1).slice(-2)}` : ""}${cicloFilter ? ` · ${cicloFilter}` : ""}${settoreFilter.length ? ` · ${settoreFilter.join("+")}` : ""}${classeFilter.length ? ` · ${classeFilter.join("+")}` : ""}`}
        anno={localAnno} />

      {/* Table */}
      <div className="sw-table-container">
        <table className="sw-table compact">
          <thead className="thead-blue">
            <tr>
              <th style={{ width: 28, textAlign: "center" }}>
                <input type="checkbox" data-testid="alunni-selall"
                  title="Seleziona/Deseleziona tutti i visibili"
                  checked={filteredAlunni.length > 0 && filteredAlunni.every(a => sel.isSelected(a.Cod))}
                  onChange={(e) => e.target.checked ? sel.selectAll(filteredAlunni.map(a => a.Cod)) : sel.deselectAll(filteredAlunni.map(a => a.Cod))} />
              </th>
              <th style={{width:30}}>#</th>
              <th>Cod</th>
              {isAgevMode && (
                <th data-testid="th-dichiarazione" title={`Stato dichiarazione famiglia per anno ${iseeFilterAnno}: 📤 inviata · ✍ firmata`}
                  style={{width: 96, textAlign:"center", background:"#e8f5e9", color:"#1b5e20"}}>DICH.<br/><span style={{fontSize:12,fontWeight:700}}>INV · FIR</span></th>
              )}
              <th>Cognome</th>
              <th>Nome</th>
              {!isAgevMode && <th>Nascita</th>}
              {!isAgevMode && <th>CF</th>}
              <th>Sett.</th>
              <th>Classe</th>
              {!isAgevMode && <th>Telefono</th>}
              {!isAgevMode && <th style={{width:50, textAlign:"center"}} title="Apri dettaglio alunno (settore/classe + email genitori)">🔍</th>}
              {showTotali && <>
                <th className="sortable num-col" onClick={()=>toggleSort("tot_addebiti")} title="Totale addebiti">Addebiti{sortArrow("tot_addebiti")}</th>
                <th className="sortable num-col" onClick={()=>toggleSort("tot_non_pagato")} title="Non Pagato">N.Pag.{sortArrow("tot_non_pagato")}</th>
                <th className="sortable num-col" onClick={()=>toggleSort("tot_non_pagato_scaduto")} title="Non Pagato Scaduto">Scad.{sortArrow("tot_non_pagato_scaduto")}</th>
                <th className="sortable num-col" onClick={()=>toggleSort("tot_escluso")} title="Totale Escluso">Escl.{sortArrow("tot_escluso")}</th>
                <th className="num-col" style={{background:"#e3f2fd", color:"#0d47a1", width:82}}
                    title={`Movimenti Iscrizione (Cau=1) per l'anno ${localAnno}. Click sul badge per aprire il dettaglio.`}
                    data-testid="th-iscr-mov">🎟 Iscriz.</th>
              </>}
              {isAgevMode && <>
                <th data-testid="th-isee-mancante" title={`Spunta se la famiglia NON ha fornito l'ISEE per anno ${iseeFilterAnno}. Crea automaticamente la scheda BOZZA se manca.`}
                  style={{width: 36, textAlign:"center", background:"#ffebee", color:"#b71c1c"}}>ISEE<br/>MANC.</th>
                <th className="num-col" title={`Valore ISEE per anno ${iseeFilterAnno}`} data-testid="th-isee">ISEE {iseeFilterAnno}</th>
                <th className="num-col" data-testid="th-pct-contrib"
                  style={{ background: "#e1f5fe", color: "#01579b", width: 90 }}
                  title="Percentuale contribuzione = 34 + (ISEE − 0) × (100 − 34) / (32.000 − 0), clamp [34%, 100%]">
                  % Contrib.
                </th>
              </>}
              <th style={{width:80, textAlign:"center"}} title="Azioni">⚙</th>
              {showAgevUI && <th style={{width:90, textAlign:"center"}} title="Dote scheda agevolazione (input)" data-testid="th-dote-input">DOTE €</th>}
              {showAgevUI && <th className="num-col" style={{width:110}} title="Agevolazione netta (€) = totale agevolazione − dote scuola" data-testid="th-tot-agev">Agev. Netta €</th>}
              {isAgevMode && <th style={{width:100, textAlign:"center"}} title="Stato scheda agevolazione (modificabile)" data-testid="th-stato">Stato</th>}
              {isAgevMode && <>
              </>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={totalCols} className="sw-loading">Caricamento...</td></tr>
            ) : alunni.length === 0 ? (
              <tr><td colSpan={totalCols} className="sw-empty"><p>Nessun alunno trovato</p></td></tr>
            ) : raggruppato && groupedRows ? (
              groupedRows.map((g, idx) => {
                if (g.kind === "header") {
                  return (
                    <tr key={`h-${g.gid}`} data-testid={`grp-header-${g.settore}-${g.classe}`}
                      style={{ background: "#1565c0", color: "white" }}>
                      <td colSpan={totalCols}
                        style={{ padding: "5px 10px", fontWeight: 700, fontSize: 12, letterSpacing: 0.4, color: "white" }}>
                        {g.groupByClasse ? `${g.settoreLabel} — Classe ${g.classe}` : g.settoreLabel}
                      </td>
                    </tr>
                  );
                }
                if (g.kind === "subtotal") {
                  return (
                    <tr key={`s-${idx}`} data-testid={`grp-subtotal-${g.settore}-${g.classe}`}
                      style={{ background: "#e3f2fd", fontWeight: 700 }}>
                      <td colSpan={totalCols}
                        style={{ padding: "4px 10px", fontSize: 11, color: "#0d47a1", textAlign: "right" }}>
                        {g.groupByClasse
                          ? <>Totale classe {g.classe}: <span style={{ color: "#1565c0" }}>{g.count} alunni</span></>
                          : <>Totale settore {g.settore}: <span style={{ color: "#1565c0" }}>{g.count} alunni</span></>}
                      </td>
                    </tr>
                  );
                }
                const a = g.data;
                // 🆕 Feb 2026: le 3 spunte A-1/A/A+1 riflettono l'ESISTENZA della scheda
                // agevolazione ATTIVA per quell'anno (read-only). Click apre la modale.
                const codN = Number(a.Cod);
                const okCur = agevActiveSet.has(codN);
                const okNext = agevActiveSetNext.has(codN);
                const okPrev = agevActiveSetPrev.has(codN);
                const rowBg = sel.isSelected(a.Cod) ? "#f3e5f5"
                  : (okCur && okNext) ? "#c8e6c9"
                  : okCur ? "#e8f5e9"
                  : okNext ? "#e3f2fd"
                  : undefined;
                return (
                  <tr key={`${a.Cod}-${a.anno}`} data-testid={`alunno-row-${a.Cod}`} onClick={()=>setSelectedCod(a.Cod)}
                      style={{ background: rowBg }}>
                    <td className="center-col" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" data-testid={`alunni-sel-${a.Cod}`}
                        checked={sel.isSelected(a.Cod)} onChange={() => sel.toggle(a.Cod)} />
                    </td>
                    <td className="center-col" style={{color:"#888"}}>{g.indexInGroup}</td>
                    <td>{a.Cod}</td>
                    {isAgevMode && (
                      <td className="center-col" onClick={e => e.stopPropagation()}
                        style={{ background: "#e8f5e9", padding: "3px 8px", whiteSpace: "nowrap" }}>
                        <label title={`📤 Dichiarazione INVIATA al genitore (anno ${iseeFilterAnno})`}
                          style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 12, fontWeight: 800, color: "#1b5e20", cursor: "pointer", marginRight: 12 }}>
                          <input type="checkbox" data-testid={`alunno-dich-inv-${a.Cod}`}
                            checked={!!dichInviataFiltered[Number(a.Cod)]}
                            onChange={() => toggleDichiarazione(a.Cod, "inviata")}
                            style={{ accentColor: "#1b5e20", transform: "scale(1.6)", marginRight: 5 }} />
                          INV
                        </label>
                        <label title={`✍ Dichiarazione FIRMATA dal genitore (anno ${iseeFilterAnno})`}
                          style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 12, fontWeight: 800, color: "#0d47a1", cursor: "pointer" }}>
                          <input type="checkbox" data-testid={`alunno-dich-fir-${a.Cod}`}
                            checked={!!dichFirmataFiltered[Number(a.Cod)]}
                            onChange={() => toggleDichiarazione(a.Cod, "firmata")}
                            style={{ accentColor: "#0d47a1", transform: "scale(1.6)", marginRight: 5 }} />
                          FIR
                        </label>
                      </td>
                    )}
                    <td style={{fontWeight:600}}>{a.Cog}</td>
                    <td>{a.Nom}</td>
                    {!isAgevMode && <td>{a.NasDat||"-"}</td>}
                    {!isAgevMode && <td style={{fontSize:10,fontFamily:"Consolas,monospace"}}>{a.CodFis||"-"}</td>}
                    <td className="center-col">{a.settore||"-"}</td>
                    <td>{a.classe||"-"}</td>
                    {!isAgevMode && <td>{a.Tel1||a.Tel2||"-"}</td>}
                    {!isAgevMode && (
                      <td className="center-col" onClick={e => e.stopPropagation()}>
                        <button onClick={() => setSelectedCod(a.Cod)}
                          data-testid={`btn-dettaglio-${a.Cod}`}
                          title="Apri dettaglio alunno (modifica settore/classe + gestisci email genitori)"
                          style={{ background: "#00838f", color: "white", border: "none", padding: "2px 6px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>
                          🔍
                        </button>
                      </td>
                    )}
                    {showTotali && <>
                      <td className="num-col" style={{background:"#f0f4ff"}}>{a.tot_addebiti ? formatCurrency(a.tot_addebiti) : "-"}</td>
                      <td className="num-col amount-warning" style={{background:"#fffbeb"}}>{a.tot_non_pagato ? formatCurrency(a.tot_non_pagato) : "-"}</td>
                      <td className="num-col amount-negative" style={{background:"#fff0f0"}}>{a.tot_non_pagato_scaduto ? formatCurrency(a.tot_non_pagato_scaduto) : "-"}</td>
                      <td className="num-col" style={{background:"#f0f0f0", color:"#555", fontWeight:600}}>{a.tot_escluso ? formatCurrency(a.tot_escluso) : "-"}</td>
                      <td className="num-col" style={{background:"#e3f2fd", textAlign:"center"}} onClick={(e) => e.stopPropagation()}>
                        <IscrizioneBadge counts={iscrCounts[Number(a.Cod)]}
                          onClick={() => openIscrizioneModal(a.Cod)}
                          compact
                          testid={`iscr-badge-${a.Cod}`} />
                      </td>
                    </>}
                    {isAgevMode && <>
                      <td className="center-col" onClick={e => e.stopPropagation()}
                        style={{ background: !!iseeMancanteFiltered[Number(a.Cod)] ? "#b71c1c" : "#ffebee" }}>
                        <input type="checkbox" data-testid={`alunno-isee-manc-${a.Cod}`}
                          title={`Famiglia ${a.Cog} ${a.Nom}: ISEE NON fornito per anno ${iseeFilterAnno}. Click per impostare/togliere.`}
                          checked={!!iseeMancanteFiltered[Number(a.Cod)]}
                          onChange={() => toggleIseeMancante(a.Cod)}
                          style={{ accentColor: "#b71c1c", transform: "scale(1.2)" }} />
                      </td>
                      <td className="num-col" data-testid={`isee-${a.Cod}`} style={{background:"#ede7f6", color:"#4527a0", fontWeight:700}}>
                        {(() => { const v = iseeMap[String(a.CodFis || "").toUpperCase().trim()]; return v ? formatCurrency(v) : "-"; })()}
                      </td>
                      {/* 🆕 % Contribuzione = 34 + ISEE × 66/32000, clamp [34%, 100%] */}
                      {(() => {
                        const v = iseeMap[String(a.CodFis || "").toUpperCase().trim()];
                        let label = "-", color = "#bbb", bg = "#fafafa";
                        if (v != null && v !== "") {
                          const n = Number(v);
                          if (Number.isFinite(n)) {
                            let pct = 34 + n * (100 - 34) / 32000;
                            if (pct < 34) pct = 34;
                            if (pct > 100) pct = 100;
                            const isMin = pct <= 34, isMax = pct >= 100;
                            label = pct.toFixed(2).replace(".", ",") + " %";
                            color = isMax ? "#c62828" : (isMin ? "#1b5e20" : "#0d47a1");
                            bg = isMax ? "#ffebee" : (isMin ? "#e8f5e9" : "#e3f2fd");
                          }
                        }
                        return (
                          <td className="num-col" data-testid={`isee-pct-${a.Cod}`}
                            style={{ background: bg, color, fontWeight: 800, fontFamily: "Consolas, monospace" }}>
                            {label}
                          </td>
                        );
                      })()}
                    </>}
                    <td className="center-col" onClick={e => e.stopPropagation()} style={{whiteSpace:"nowrap"}}>
                      {!isAgevMode && (
                        <button onClick={() => navigate(`/g/${gestione}/movimenti?codice_alunno=${a.Cod}&anno=${a.anno}&from=${isAgevMode ? "alunni_agev" : "alunni"}`)}
                          data-testid={`btn-mov-${a.Cod}`}
                          title="Apri lista movimenti di questo alunno (ordinati per data scadenza)"
                          style={{ background: "white", color: "#1565c0", border: "1px solid #1565c0", padding: "2px 6px", fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 4, marginRight: 4, display: "inline-flex", alignItems: "center", gap: 3 }}>
                          <span role="img" aria-label="movimenti">📋</span> Mov.
                        </button>
                      )}
                      {!isAgevMode && (
                        <button onClick={() => setSchedaAlunno({ cod: a.Cod, anno: a.anno, label: `${a.Cog} ${a.Nom}` })}
                          data-testid={`btn-scheda-${a.Cod}`}
                          title="Apri la Scheda riassuntiva dell'alunno (ISEE, extra, mensa, contabilità…)"
                          style={{ background: "white", color: "#00695c", border: "1px solid #00695c", padding: "2px 6px", fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 4, marginRight: 4, display: "inline-flex", alignItems: "center", gap: 3 }}>
                          🗂 Scheda
                        </button>
                      )}
                      {/* 🗑 Bottone Genitori rimosso su richiesta utente feb-2026 (usa Alunni-Genitori tab) */}
                      {!isAgevMode && (
                        <button onClick={() => setDocsAlunno(a)}
                          data-testid={`btn-docs-${a.Cod}`}
                          title="Documenti dell&apos;alunno (cartella personale)"
                          style={{ display: "none", background: "#6a1b9a", color: "white", border: "none", padding: "2px 6px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2, marginRight: 4 }}>
                          📁
                        </button>
                      )}
                      {showAgevUI && (() => {
                        const codNum = Number(a.Cod);
                        const isActive = agevActiveSet.has(codNum);
                        const st = isActive ? (agevStati[codNum] || "BOZZA") : null;
                        const dote = agevDote[codNum] || 0;
                        // 🆕 Feb 2026: bottone AGEVOLAZIONE ampio con 2 stati chiari:
                        //   NON ATTIVA → rosso chiaro con "＋ AGEVOLAZIONE"
                        //   ATTIVA     → colore proprio dello stato + iniziale (B/A/F/R/…)
                        let bg = "#ffebee", color = "#c62828", border = "1px solid #c62828",
                            label = "＋ AGEVOLAZIONE", title = "Nessuna scheda — clicca per crearla",
                            boxShadow = "none";
                        if (isActive) {
                          const cfgSt = agevStatoCfg(st || "BOZZA");
                          bg = cfgSt.bg; color = cfgSt.color; border = "1px solid rgba(0,0,0,0.15)";
                          const ini =
                            st === "APPROVATA"    ? "✓" :
                            st === "BOZZA"        ? "B" :
                            st === "SC_FRATELLI"  ? "F" :
                            st === "FIRMATA"      ? "✎" :
                            st === "RATE"         ? "R" :
                            st === "NO"           ? "✗" :
                            st === "USCITO"       ? "U" :
                            st === "APPUNTAMENTO" ? "A" :
                                                     "•";
                          if (st === "APPROVATA") { bg = "#2e7d32"; color = "white"; boxShadow = "0 0 0 2px #a5d6a7"; }
                          label = `${ini} AGEVOLAZIONE`;
                          title = `Scheda ${cfgSt.ui || st} — clicca per aprire/modificare`;
                        }
                        return (
                          <button onClick={async () => {
                            if (!isActive) {
                              setAgevPicker({ cod: a.Cod });
                            } else {
                              setAgevAnno(a.anno);
                              setAgevCod(a.Cod);
                            }
                          }}
                            data-testid={`btn-agev-${a.Cod}`}
                            title={title + (dote > 0 ? ` · Dote: €${dote}` : "")}
                            style={{
                              background: bg, color, border,
                              padding: "3px 8px", fontSize: 10, fontWeight: 900,
                              cursor: "pointer", borderRadius: 4, letterSpacing: 0.3,
                              boxShadow,
                            }}>
                            {label}
                            {dote > 0 && <span style={{ marginLeft: 3, opacity: 0.85, fontSize: 9 }}>🎓</span>}
                          </button>
                        );
                      })()}
                      {showAgevUI && agevActiveSet.has(Number(a.Cod)) && (
                        <button onClick={() => setAgevMail({ cod: a.Cod, anno: a.anno })}
                          data-testid={`btn-agev-mail-${a.Cod}`}
                          title="Invia la scheda Agevolazione via email (PDF allegato)"
                          style={{ background: "#0d47a1", color: "white", border: "none", padding: "3px 8px", fontSize: 10, fontWeight: 800, cursor: "pointer", borderRadius: 4, marginLeft: 4 }}>
                          📧 Mail
                        </button>
                      )}
                    </td>
                    {showAgevUI && (<>
                    <td className="center-col" onClick={e => e.stopPropagation()}>
                      <DoteInputCell
                        cod={a.Cod}
                        anno={a.anno}
                        value={agevDote[Number(a.Cod)] || 0}
                        editValue={doteEdit[`${a.Cod}-${a.anno}`]}
                        setEditValue={(v) => setDoteEdit(m => ({ ...m, [`${a.Cod}-${a.anno}`]: v }))}
                        saving={!!doteSaving[`${a.Cod}-${a.anno}`]}
                        onSave={(v) => saveDote(a.Cod, a.anno, v)}
                        locked={false}
                      />
                    </td>
                    <td className="num-col" data-testid={`tot-agev-${a.Cod}`}
                        style={(() => {
                          const v = agevNetta[Number(a.Cod)];
                          if (v && Number(v) < 0) return { background:"#1b5e20", color:"white", fontWeight:700 };
                          if (v && Number(v) > 0) return { background:"#c62828", color:"white", fontWeight:700 };
                          return { background:"#f1f8e9", color:"#1b5e20", fontWeight:600 };
                        })()}>
                      {(() => { const v = agevNetta[Number(a.Cod)]; return v ? formatCurrency(v) : "-"; })()}
                    </td>
                    </>)}
                    {isAgevMode && (
                      <td className="center-col" onClick={e => e.stopPropagation()} data-testid={`stato-${a.Cod}`}>
                        {(() => {
                          const st = agevStati[Number(a.Cod)];
                          const active = agevActiveSet.has(Number(a.Cod));
                          if (!active) return <span style={{fontSize:10,color:"#999"}}>—</span>;
                          const ui = agevStatoCfg(st || "BOZZA").ui;
                          const cfg = agevStatoCfg(st || "BOZZA");
                          return (
                            <select value={agevSelectValue(st)} onChange={e => cambiaStato(a.Cod, e.target.value)}
                              style={{
                                fontWeight: 700, fontSize: 10, padding: "1px 3px",
                                border: "1px solid #ccc", borderRadius: 3,
                                background: cfg.bg,
                                color: cfg.color,
                                cursor: "pointer",
                              }}>
                              {AGEV_STATI_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          );
                        })()}
                      </td>
                    )}
                    {isAgevMode && <>
                    </>}
                  </tr>
                );
              })
            ) : filteredAlunni.map((a, i) => {
              // 🆕 Feb 2026: le 3 spunte A-1/A/A+1 riflettono l'ESISTENZA della scheda
              // agevolazione ATTIVA per quell'anno (read-only). Click apre la modale.
              const codN = Number(a.Cod);
              const okCur = agevActiveSet.has(codN);
              const okNext = agevActiveSetNext.has(codN);
              const okPrev = agevActiveSetPrev.has(codN);
              const rowBg = sel.isSelected(a.Cod) ? "#f3e5f5"
                : (okCur && okNext) ? "#c8e6c9"
                : okCur ? "#e8f5e9"
                : okNext ? "#e3f2fd"
                : undefined;
              return (
              <tr key={`${a.Cod}-${a.anno}`} data-testid={`alunno-row-${a.Cod}`} onClick={()=>setSelectedCod(a.Cod)}
                  style={{ background: rowBg }}>
                <td className="center-col" onClick={e => e.stopPropagation()}>
                  <input type="checkbox" data-testid={`alunni-sel-${a.Cod}`}
                    checked={sel.isSelected(a.Cod)} onChange={() => sel.toggle(a.Cod)} />
                </td>
                <td className="center-col" style={{color:"#888"}}>{(page-1)*limit+i+1}</td>
                <td>{a.Cod}</td>
                {isAgevMode && (
                  <td className="center-col" onClick={e => e.stopPropagation()}
                    style={{ background: "#e8f5e9", padding: "3px 8px", whiteSpace: "nowrap" }}>
                    <label title={`📤 Dichiarazione INVIATA al genitore (anno ${iseeFilterAnno})`}
                      style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 12, fontWeight: 800, color: "#1b5e20", cursor: "pointer", marginRight: 12 }}>
                      <input type="checkbox" data-testid={`alunno-dich-inv-${a.Cod}`}
                        checked={!!dichInviataFiltered[Number(a.Cod)]}
                        onChange={() => toggleDichiarazione(a.Cod, "inviata")}
                        style={{ accentColor: "#1b5e20", transform: "scale(1.6)", marginRight: 5 }} />
                      INV
                    </label>
                    <label title={`✍ Dichiarazione FIRMATA dal genitore (anno ${iseeFilterAnno})`}
                      style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 12, fontWeight: 800, color: "#0d47a1", cursor: "pointer" }}>
                      <input type="checkbox" data-testid={`alunno-dich-fir-${a.Cod}`}
                        checked={!!dichFirmataFiltered[Number(a.Cod)]}
                        onChange={() => toggleDichiarazione(a.Cod, "firmata")}
                        style={{ accentColor: "#0d47a1", transform: "scale(1.6)", marginRight: 5 }} />
                      FIR
                    </label>
                  </td>
                )}
                <td style={{fontWeight:600}}>{a.Cog}</td>
                <td>{a.Nom}</td>
                {!isAgevMode && <td>{a.NasDat||"-"}</td>}
                {!isAgevMode && <td style={{fontSize:10,fontFamily:"Consolas,monospace"}}>{a.CodFis||"-"}</td>}
                <td className="center-col">{a.settore||"-"}</td>
                <td>{a.classe||"-"}</td>
                {!isAgevMode && <td>{a.Tel1||a.Tel2||"-"}</td>}
                {!isAgevMode && (
                  <td className="center-col" onClick={e => e.stopPropagation()}>
                    <button onClick={() => setSelectedCod(a.Cod)}
                      data-testid={`btn-dettaglio-${a.Cod}`}
                      title="Apri dettaglio alunno (modifica settore/classe + gestisci email genitori)"
                      style={{ background: "#00838f", color: "white", border: "none", padding: "2px 6px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>
                      🔍
                    </button>
                  </td>
                )}
                {showTotali && <>
                  <td className="num-col" style={{background:"#f0f4ff"}}>{a.tot_addebiti ? formatCurrency(a.tot_addebiti) : "-"}</td>
                  <td className="num-col amount-warning" style={{background:"#fffbeb"}}>{a.tot_non_pagato ? formatCurrency(a.tot_non_pagato) : "-"}</td>
                  <td className="num-col amount-negative" style={{background:"#fff0f0"}}>{a.tot_non_pagato_scaduto ? formatCurrency(a.tot_non_pagato_scaduto) : "-"}</td>
                  <td className="num-col" style={{background:"#f0f0f0", color:"#555", fontWeight:600}}>{a.tot_escluso ? formatCurrency(a.tot_escluso) : "-"}</td>
                  <td className="num-col" style={{background:"#e3f2fd", textAlign:"center"}} onClick={(e) => e.stopPropagation()}>
                    <IscrizioneBadge counts={iscrCounts[Number(a.Cod)]}
                      onClick={() => openIscrizioneModal(a.Cod)}
                      compact
                      testid={`iscr-badge-grp-${a.Cod}`} />
                  </td>
                </>}
                {isAgevMode && <>
                  <td className="center-col" onClick={e => e.stopPropagation()}
                    style={{ background: !!iseeMancanteFiltered[Number(a.Cod)] ? "#b71c1c" : "#ffebee" }}>
                    <input type="checkbox" data-testid={`alunno-isee-manc-${a.Cod}`}
                      title={`Famiglia ${a.Cog} ${a.Nom}: ISEE NON fornito per anno ${iseeFilterAnno}.`}
                      checked={!!iseeMancanteFiltered[Number(a.Cod)]}
                      onChange={() => toggleIseeMancante(a.Cod)}
                      style={{ accentColor: "#b71c1c", transform: "scale(1.2)" }} />
                  </td>
                  <td className="num-col" data-testid={`isee-${a.Cod}`} style={{background:"#ede7f6", color:"#4527a0", fontWeight:700}}>
                    {(() => { const v = iseeMap[String(a.CodFis || "").toUpperCase().trim()]; return v ? formatCurrency(v) : "-"; })()}
                  </td>
                  {/* 🆕 % Contribuzione */}
                  {(() => {
                    const v = iseeMap[String(a.CodFis || "").toUpperCase().trim()];
                    let label = "-", color = "#bbb", bg = "#fafafa";
                    if (v != null && v !== "") {
                      const n = Number(v);
                      if (Number.isFinite(n)) {
                        let pct = 34 + n * (100 - 34) / 32000;
                        if (pct < 34) pct = 34;
                        if (pct > 100) pct = 100;
                        const isMin = pct <= 34, isMax = pct >= 100;
                        label = pct.toFixed(2).replace(".", ",") + " %";
                        color = isMax ? "#c62828" : (isMin ? "#1b5e20" : "#0d47a1");
                        bg = isMax ? "#ffebee" : (isMin ? "#e8f5e9" : "#e3f2fd");
                      }
                    }
                    return (
                      <td className="num-col" data-testid={`isee-pct-${a.Cod}`}
                        style={{ background: bg, color, fontWeight: 800, fontFamily: "Consolas, monospace" }}>
                        {label}
                      </td>
                    );
                  })()}
                </>}
                <td className="center-col" onClick={e => e.stopPropagation()} style={{whiteSpace:"nowrap"}}>
                  {!isAgevMode && (
                    <button onClick={() => navigate(`/g/${gestione}/movimenti?codice_alunno=${a.Cod}&anno=${a.anno}&from=${isAgevMode ? "alunni_agev" : "alunni"}`)}
                      data-testid={`btn-mov-${a.Cod}`}
                      title="Apri lista movimenti di questo alunno (ordinati per data scadenza)"
                      style={{ background: "white", color: "#1565c0", border: "1px solid #1565c0", padding: "2px 6px", fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 4, marginRight: 4, display: "inline-flex", alignItems: "center", gap: 3 }}>
                      <span role="img" aria-label="movimenti">📋</span> Mov.
                    </button>
                  )}
                  {/* 🗑 Bottone Genitori rimosso su richiesta utente feb-2026 (usa Alunni-Genitori tab) */}
                  {!isAgevMode && (
                    <button onClick={() => setDocsAlunno(a)}
                      data-testid={`btn-docs-${a.Cod}`}
                      title="Documenti dell&apos;alunno"
                      style={{ display: "none", background: "#6a1b9a", color: "white", border: "none", padding: "2px 6px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2, marginRight: 4 }}>
                      📁
                    </button>
                  )}
                  {/* 🆕 Feb 2026: bottone Agevolazione più evidente, testo "AGEVOLAZIONE",
                       colori distinti: ATTIVA (verde forte) vs NON ATTIVA (rosso tenue). */}
                  {showAgevUI && (() => {
                    const active = agevActiveSet.has(Number(a.Cod));
                    return (
                      <button onClick={() => {
                          if (active) {
                            setAgevAnno(a.anno);
                            setAgevCod(a.Cod);
                          } else {
                            setAgevPicker({ cod: a.Cod });
                          }
                        }}
                        data-testid={`btn-agev-${a.Cod}`}
                        title={active ? "Scheda Agevolazione ATTIVA — clicca per modificare" : "Nessuna scheda attiva — clicca per crearne una"}
                        style={{
                          background: active ? "#2e7d32" : "#ffebee",
                          color: active ? "white" : "#c62828",
                          border: active ? "1px solid #1b5e20" : "1px solid #c62828",
                          padding: "3px 8px",
                          fontSize: 10,
                          fontWeight: 900,
                          cursor: "pointer",
                          borderRadius: 4,
                          letterSpacing: 0.3,
                          boxShadow: active ? "0 0 0 2px #a5d6a7" : "none",
                          textShadow: active ? "0 1px 0 rgba(0,0,0,0.15)" : "none",
                        }}>
                        {active ? "✓ AGEVOLAZIONE" : "＋ AGEVOLAZIONE"}
                      </button>
                    );
                  })()}
                  {showAgevUI && agevActiveSet.has(Number(a.Cod)) && (
                    <button onClick={() => setAgevMail({ cod: a.Cod, anno: a.anno })}
                      data-testid={`btn-agev-mail-${a.Cod}`}
                      title="Invia la scheda Agevolazione via email (PDF allegato)"
                      style={{ background: "#0d47a1", color: "white", border: "none", padding: "3px 8px", fontSize: 10, fontWeight: 800, cursor: "pointer", borderRadius: 4, marginLeft: 4 }}>
                      📧 Mail
                    </button>
                  )}
                </td>
                {showAgevUI && (<>
                <td className="center-col" onClick={e => e.stopPropagation()}>
                  <DoteInputCell
                    cod={a.Cod}
                    anno={a.anno}
                    value={agevDote[Number(a.Cod)] || 0}
                    editValue={doteEdit[`${a.Cod}-${a.anno}`]}
                    setEditValue={(v) => setDoteEdit(m => ({ ...m, [`${a.Cod}-${a.anno}`]: v }))}
                    saving={!!doteSaving[`${a.Cod}-${a.anno}`]}
                    onSave={(v) => saveDote(a.Cod, a.anno, v)}
                    locked={agevStati[Number(a.Cod)] === "APPROVATA"}
                  />
                </td>
                <td className="num-col" data-testid={`tot-agev-${a.Cod}`}
                    style={(() => {
                      const v = agevNetta[Number(a.Cod)];
                      if (v && Number(v) < 0) return { background:"#1b5e20", color:"white", fontWeight:700 };
                      if (v && Number(v) > 0) return { background:"#c62828", color:"white", fontWeight:700 };
                      return { background:"#f1f8e9", color:"#1b5e20", fontWeight:600 };
                    })()}>
                  {(() => { const v = agevNetta[Number(a.Cod)]; return v ? formatCurrency(v) : "-"; })()}
                </td>
                </>)}
                {isAgevMode && (
                  <td className="center-col" onClick={e => e.stopPropagation()} data-testid={`stato-${a.Cod}`}>
                    {(() => {
                      const st = agevStati[Number(a.Cod)];
                      const active = agevActiveSet.has(Number(a.Cod));
                      if (!active) return <span style={{fontSize:10,color:"#999"}}>—</span>;
                      const ui = agevStatoCfg(st || "BOZZA").ui;
                      const cfg = agevStatoCfg(st || "BOZZA");
                      return (
                        <select value={agevSelectValue(st)} onChange={e => cambiaStato(a.Cod, e.target.value)}
                          style={{
                            fontWeight: 700, fontSize: 10, padding: "1px 3px",
                            border: "1px solid #ccc", borderRadius: 3,
                            background: cfg.bg,
                            color: cfg.color,
                            cursor: "pointer",
                          }}>
                          {AGEV_STATI_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      );
                    })()}
                  </td>
                )}
                {isAgevMode && <>
                </>}
              </tr>
            );})}
            {/* Totale generale finale solo in modalità raggruppata */}
            {raggruppato && groupedRows && alunni.length > 0 && (
              <tr data-testid="alunni-totale-generale" style={{ background: "#fff59d", fontWeight: 700 }}>
                <td colSpan={totalCols} style={{ padding: "6px 10px", fontSize: 12, color: "#3e2723", textAlign: "right" }}>
                  TOTALE GENERALE: <span style={{ color: "#1565c0" }}>{filtroAgev ? filteredAlunni.length : alunni.length} alunni</span>
                  {filtroAgev && <span style={{ color: "#888", marginLeft: 8, fontSize: 11 }}>(filtrati su {alunni.length})</span>}
                </td>
              </tr>
            )}
            {/* Riga somma verticale Totale Agevolazione (gestione agevolazioni o modalità agev) */}
            {showAgevUI && alunni.length > 0 && (
              <tr data-testid="alunni-totale-agev-row" style={{ background: "#c8e6c9", fontWeight: 800 }}>
                <td colSpan={totalCols - 1}
                    style={{ padding: "6px 10px", fontSize: 12, color: "#1b5e20", textAlign: "right" }}>
                  TOTALE AGEV. NETTA ({(raggruppato ? (filtroAgev ? filteredAlunni.length : alunni.length) : (filteredAlunni?.length || 0))} alunni):
                </td>
                <td className="num-col" data-testid="alunni-totale-agev-sum" style={{ padding: "6px 8px", fontSize: 12, color: "#1b5e20", background: "#a5d6a7" }}>
                  {formatCurrency(totaleAgevSomma)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {!raggruppato && total > 0 && (
          <div className="sw-pagination">
            <span>{total} alunn{total === 1 ? "o" : "i"}</span>
          </div>
        )}
      </div>

      {selectedCod && <AlunnoDetail cod={selectedCod} anno={localAnno} onClose={()=>setSelectedCod(null)} gestione={gestione} />}
      {emailsCod !== null && <AlunnoEmailsModal cod={emailsCod} alunnoLabel={emailsLabel} onClose={() => setEmailsCod(null)} />}
      {docsAlunno && <DocumentiAlunnoModal alunno={docsAlunno} close={() => setDocsAlunno(null)} />}
      {schedaAlunno && (
        <div data-testid="scheda-alunno-modal" onClick={() => setSchedaAlunno(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 900, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "24px 16px", overflowY: "auto" }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: "#f8fafc", width: "100%", maxWidth: 980, borderRadius: 12, overflow: "hidden", boxShadow: "0 10px 40px rgba(0,0,0,0.3)" }}>
            <div style={{ background: "#1e3a8a", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong style={{ fontSize: 14 }}>🗂 Scheda Alunno · {schedaAlunno.label}</strong>
              <button onClick={() => setSchedaAlunno(null)} data-testid="scheda-alunno-close"
                style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ padding: 16 }}>
              <SchedaRiassuntiva alunnoCod={schedaAlunno.cod} anno={schedaAlunno.anno} />
            </div>
          </div>
        </div>
      )}
      {iscrModal && <MovimentiCollegatiModal data={iscrModal} onClose={() => setIscrModal(null)} />}
      {agevCod !== null && (
        <AgevolazioneModal
          alunnoCod={agevCod}
          annoInitial={agevAnno != null ? agevAnno : localAnno}
          onClose={() => { setAgevCod(null); setAgevAnno(null); }}
          onSaved={() => { reloadAgevSet(); reloadAgevSetNext(); }}
        />
      )}
      {agevMail && (
        <AgevMailModal cod={agevMail.cod} anno={agevMail.anno} onClose={() => setAgevMail(null)} />
      )}
      {agevPicker && (
        <AnnoSchedaPicker
          annoCorrente={localAnno}
          onCancel={() => setAgevPicker(null)}
          onPick={(anno) => {
            const cod = agevPicker.cod;
            setAgevPicker(null);
            setAgevAnno(anno);
            setAgevCod(cod);
          }}
        />
      )}
    </div>
  );
}


function DoteInputCell({ cod, anno, value, editValue, setEditValue, saving, onSave, locked }) {
  const isEditing = editValue !== undefined;
  const current = isEditing ? editValue : value;
  const commit = () => {
    const newVal = Number(current) || 0;
    if (newVal !== Number(value)) onSave(newVal);
    else setEditValue(undefined);
  };
  if (locked) {
    return (
      <span title="Scheda APPROVATA — riapri per modificare la dote"
            style={{ fontSize: 11, fontWeight: 700, color: "#1b5e20", fontFamily: "Consolas, monospace" }}>
        🔒 {Number(value) > 0 ? Number(value).toLocaleString("it-IT") : "—"}
      </span>
    );
  }
  return (
    <input
      type="number" step="0.01" min="0"
      value={current === 0 || current ? current : ""}
      onChange={(e) => setEditValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.target.blur(); }
        if (e.key === "Escape") setEditValue(undefined);
      }}
      placeholder="0"
      disabled={saving}
      data-testid={`dote-input-${cod}`}
      style={{
        width: 76, padding: "2px 4px",
        border: "1px solid " + (Number(value) > 0 ? "#1565c0" : "#cfd8dc"),
        borderRadius: 2, fontSize: 11, textAlign: "right",
        fontFamily: "Consolas, monospace", fontWeight: 600,
        background: saving ? "#fff9c4" : (Number(value) > 0 ? "#e3f2fd" : "white"),
      }}
    />
  );
}


// Picker per scegliere l'anno scolastico della NUOVA scheda agevolazione.
// L'anno NON è proposto automaticamente: l'utente deve selezionarlo prima
// che si apra il modal. La scheda non viene creata finché non si preme SALVA.
function AnnoSchedaPicker({ annoCorrente, onCancel, onPick }) {
  const base = Number(annoCorrente) || new Date().getFullYear();
  const years = [base - 1, base, base + 1, base + 2];
  const [scelto, setScelto] = useState(null);
  const annoScolLabel = (a) => `${a}/${String((Number(a) || 0) + 1).slice(-2)}`;
  return (
    <div data-testid="agev-anno-picker" onClick={onCancel}
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 950,
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: "white", width: 440, maxWidth: "100%", borderRadius: 6, overflow: "hidden", boxShadow: "0 10px 40px rgba(0,0,0,0.3)" }}>
        <div style={{ background: "linear-gradient(90deg,#1b5e20,#2e7d32)", color: "white", padding: "12px 16px" }}>
          <div style={{ fontSize: 14, fontWeight: 800 }}>💰 Nuova scheda agevolazione</div>
          <div style={{ fontSize: 11, opacity: 0.9, marginTop: 3 }}>
            Seleziona l&apos;anno scolastico per cui creare la scheda.
          </div>
        </div>
        <div style={{ padding: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#666", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.3 }}>
            Anno scolastico
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {years.map((y) => (
              <button key={y}
                      data-testid={`agev-anno-pick-${y}`}
                      onClick={() => setScelto(y)}
                      style={{
                        padding: "12px 8px",
                        background: scelto === y ? "#1b5e20" : "white",
                        color: scelto === y ? "white" : "#1b5e20",
                        border: `2px solid ${scelto === y ? "#1b5e20" : "#a5d6a7"}`,
                        fontSize: 14, fontWeight: 800, cursor: "pointer",
                        borderRadius: 4, letterSpacing: 0.3,
                      }}>
                {annoScolLabel(y)}{y === base && <span style={{ display: "block", fontSize: 9, fontWeight: 600, opacity: 0.8, marginTop: 2 }}>(corrente)</span>}
              </button>
            ))}
          </div>
          <div style={{ marginTop: 12, padding: "8px 10px", background: "#e3f2fd", border: "1px solid #90caf9", borderRadius: 3, fontSize: 11, color: "#0d47a1" }}>
            ℹ️ La scheda verrà effettivamente creata solo quando premerai <b>SALVA</b> nel modulo successivo.
          </div>
        </div>
        <div style={{ padding: 10, background: "#fafafa", borderTop: "1px solid #eee", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onCancel} data-testid="agev-anno-pick-annulla"
                  style={{ padding: "8px 16px", background: "white", color: "#666", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
            Annulla
          </button>
          <button onClick={() => scelto != null && onPick(scelto)}
                  disabled={scelto == null}
                  data-testid="agev-anno-pick-conferma"
                  style={{ padding: "8px 20px",
                           background: scelto != null ? "#2e7d32" : "#bdbdbd",
                           color: "white", border: "none", fontSize: 13, fontWeight: 800,
                           cursor: scelto != null ? "pointer" : "not-allowed", borderRadius: 3 }}>
            ➜ Apri scheda
          </button>
        </div>
      </div>
    </div>
  );
}
